import React, {
  useCallback,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Platform,
  ActivityIndicator,
  useWindowDimensions,
  Modal,
  FlatList,
} from 'react-native';

import { GroupsTab } from './users/GroupsTab';
import { Header } from '../components/Header';
import { SubHeader } from '../components/SubHeader';
import {
  Search,
  RefreshCw,
  AlertCircle,
  Edit,
  Save,
  X,
  Filter,
  ChevronDown,
  UserPlus,
  Check,
  Eye,
} from './../components/icons';
import { ImpersonationModePicker } from '../components/impersonation/ImpersonationModePicker';
import {
  KeyboardDismissToolbar,
  ACCESSORY_VIEW_ID,
} from '../components/shared/KeyboardDismissToolbar';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import {
  useUsers,
  useRoles,
  useAssignRole,
  useRemoveRole,
  UserWithRoles,
  LastLoginTimeRange,
} from '../hooks/useRoleManagement';
import { roleService } from '../services/auth/roleService';
import { layout } from '../theme/spacing';
import { AppRole } from '../types/auth';
import { ImpersonationMode } from '../types/Impersonation';
import { dateFormatting } from '../utils/dateFormatting';
import {
  hasLvoEligibleChannelRole,
  isLvoColumnChecked,
  LVO_COLUMN_ACCESSIBILITY_LABEL,
  LVO_COLUMN_KEY,
  LVO_COLUMN_LABEL,
  isLvoPairedRoleName,
  removePairedLvoWhenChannelRemoved,
  toggleLvoColumnInSelection,
} from '../utils/roleManagementLvoColumn';
import { showToast } from '../utils/toast';
import { formatTwoLineTableHeader } from '../utils/twoLineTableHeader';

const isWeb = Platform.OS === 'web';

const TABLE_SCROLL_TRACK_SIZE = 10;
const TABLE_SCROLL_THUMB_SIZE = 6;
const TABLE_SCROLL_TRACK_RADIUS = 5;
const TABLE_SCROLL_THUMB_RADIUS = 3;
const TABLE_SCROLL_TRACK_COLOR = 'rgba(73, 80, 87, 0.12)';
const TABLE_SCROLL_THUMB_COLOR = 'rgba(73, 80, 87, 0.55)';
const TABLE_INDICATOR_GUTTER = layout.users.tableIndicatorGutter;
const TABLE_HEADER_BACKGROUND = '#f8f9fa';
const TABLE_HEADER_BORDER = '#dee2e6';

const USER_COLUMN_WIDTH = layout.users.userColumnWidth;
const DATE_COLUMN_WIDTH = layout.users.dateColumnWidth;
const ROLE_COLUMN_WIDTH = layout.users.roleColumnWidth;
const ACTION_COLUMN_WIDTH = layout.users.actionColumnWidth;
const TABLE_HORIZONTAL_PADDING = layout.users.tableHorizontalPadding;
const TABLE_SECTION_HORIZONTAL_PADDING =
  layout.users.tableSectionHorizontalPadding;
const TABLE_SECTION_VERTICAL_PADDING = layout.users.tableSectionVerticalPadding;

const FROZEN_USER_COLUMN_WEB = {
  position: 'sticky' as unknown as undefined,
  left: 0,
};

const FROZEN_USER_HEADER_WEB = {
  ...FROZEN_USER_COLUMN_WEB,
  top: 0,
  zIndex: 4,
};

const WEB_TABLE_HEADER_STICKY = {
  position: 'sticky' as unknown as undefined,
  top: 0,
  zIndex: 3,
};

const WEB_TABLE_SCROLLPORT_STYLE = {
  overflow: 'auto' as unknown as 'scroll',
  scrollbarWidth: 'none' as unknown as undefined,
  msOverflowStyle: 'none' as unknown as undefined,
};

interface WebTableScrollportNode {
  scrollLeft: number;
  scrollTop: number;
  scrollTo: (options: {
    x?: number;
    y?: number;
    left?: number;
    top?: number;
    animated?: boolean;
  }) => void;
}

const readTableScrollOffset = (
  event: {
    nativeEvent?: { contentOffset?: { x?: number; y?: number } };
    currentTarget?: { scrollLeft?: number; scrollTop?: number };
  },
  axis: 'x' | 'y',
): number => {
  const fromNative =
    axis === 'x'
      ? event.nativeEvent?.contentOffset?.x
      : event.nativeEvent?.contentOffset?.y;
  if (typeof fromNative === 'number') {
    return fromNative;
  }
  const target = event.currentTarget;
  return axis === 'x' ? target?.scrollLeft ?? 0 : target?.scrollTop ?? 0;
};

function getUserListDisplayName(userRow: UserWithRoles): string {
  if (userRow.firstName || userRow.lastName) {
    return `${userRow.firstName || ''} ${userRow.lastName || ''}`.trim();
  }
  return userRow.email;
}

// Explicit column order for role management table
const ROLE_DISPLAY_ORDER: string[] = [
  AppRole.SUPERVISOR_NAM,
  AppRole.SUPERVISOR_EMEA,
  AppRole.SUPERVISOR_APAC,
  AppRole.REQUESTOR,
  AppRole.EXTERNAL_REQUESTOR,
  AppRole.VERTIV_CONTRIBUTOR,
  AppRole.PARTNER_CONTRIBUTOR,
  AppRole.CONFIG_ENGINEER,
  AppRole.VERTIV_SPONSOR,
  AppRole.IT_ADMINISTRATOR,
  AppRole.BUSINESS_OWNER,
  AppRole.ROLE_MANAGER,
  AppRole.IMPERSONATOR_FULL_ACCESS,
  AppRole.IMPERSONATOR_READ_ONLY,
];

// Must match PRIVILEGED_ROLE_NAMES in server/utils/roleChangePolicy.ts: only an
// IT Administrator may grant or remove these, and their holders cannot be
// impersonated.
const PRIVILEGED_ROLE_NAMES: ReadonlySet<string> = new Set([
  AppRole.IT_ADMINISTRATOR,
  AppRole.ROLE_MANAGER,
  AppRole.IMPERSONATOR_FULL_ACCESS,
  AppRole.IMPERSONATOR_READ_ONLY,
]);

interface RoleManagementProps {
  navigate: (path: string) => void;
  currentPath?: string;
}

// Track editing state for each user row
interface EditingState {
  userId: number;
  selectedRoles: Set<string>; // Role names that are checked
}

type UsersTableRoleColumn =
  | { type: 'role'; role: { id: number; name: string } }
  | { type: 'lvo' };

interface TableScrollIndicatorHandle {
  setHorizontalOffset: (offset: number) => void;
  setVerticalOffset: (offset: number) => void;
}

interface TableScrollIndicatorsProps {
  bodyContentHeight: number;
  bodyViewportHeight: number;
  contentWidth: number;
  headerHeight: number;
  onHorizontalScrollTo: (offset: number) => void;
  onVerticalScrollTo: (offset: number) => void;
  viewportWidth: number;
}

type TableScrollAxis = 'horizontal' | 'vertical';

interface TableScrollDragState {
  axis: TableScrollAxis;
  maxScroll: number;
  pointerStart: number;
  scrollStart: number;
  thumbTravel: number;
}

interface TableScrollMouseEvent {
  button?: number;
  clientX?: number;
  clientY?: number;
  currentTarget?: {
    getBoundingClientRect?: () => { left: number; top: number };
  };
  defaultPrevented?: boolean;
  nativeEvent?: {
    button?: number;
    clientX?: number;
    clientY?: number;
    deltaX?: number;
    deltaY?: number;
    offsetX?: number;
    offsetY?: number;
    pageX?: number;
    pageY?: number;
  };
  preventDefault?: () => void;
  stopPropagation?: () => void;
}

const TableScrollIndicators = forwardRef<
  TableScrollIndicatorHandle,
  TableScrollIndicatorsProps
>(function TableScrollIndicators(
  {
    bodyContentHeight,
    bodyViewportHeight,
    contentWidth,
    headerHeight,
    onHorizontalScrollTo,
    onVerticalScrollTo,
    viewportWidth,
  },
  ref,
) {
  const isWebPlatform = Platform.OS === 'web';
  const [horizontalOffset, setHorizontalOffset] = useState(0);
  const [verticalOffset, setVerticalOffset] = useState(0);
  const dragStateRef = useRef<TableScrollDragState | null>(null);
  const scrollToOffsetRef = useRef<
    (axis: TableScrollAxis, requestedOffset: number) => void
  >(() => undefined);
  const maxScrollX = Math.max(contentWidth - viewportWidth, 0);
  const maxScrollY = Math.max(bodyContentHeight - bodyViewportHeight, 0);

  const scrollToOffset = useCallback(
    (axis: TableScrollAxis, requestedOffset: number) => {
      const maxScroll = axis === 'horizontal' ? maxScrollX : maxScrollY;
      const offset = Math.min(Math.max(requestedOffset, 0), maxScroll);

      if (axis === 'horizontal') {
        setHorizontalOffset(offset);
        onHorizontalScrollTo(offset);
      } else {
        setVerticalOffset(offset);
        onVerticalScrollTo(offset);
      }
    },
    [maxScrollX, maxScrollY, onHorizontalScrollTo, onVerticalScrollTo],
  );
  scrollToOffsetRef.current = scrollToOffset;

  useImperativeHandle(
    ref,
    () => ({
      setHorizontalOffset: offset =>
        setHorizontalOffset(Math.min(Math.max(offset, 0), maxScrollX)),
      setVerticalOffset: offset =>
        setVerticalOffset(Math.min(Math.max(offset, 0), maxScrollY)),
    }),
    [maxScrollX, maxScrollY],
  );

  useEffect(() => {
    setHorizontalOffset(offset => Math.min(Math.max(offset, 0), maxScrollX));
  }, [maxScrollX]);

  useEffect(() => {
    setVerticalOffset(offset => Math.min(Math.max(offset, 0), maxScrollY));
  }, [maxScrollY]);

  useEffect(() => {
    dragStateRef.current = null;
  }, [
    bodyContentHeight,
    bodyViewportHeight,
    contentWidth,
    maxScrollX,
    maxScrollY,
    viewportWidth,
  ]);

  useEffect(() => {
    if (!isWebPlatform) return;

    const doc = globalThis.document;
    const win = globalThis.window;
    if (!doc || !win) return;

    const handleMouseMove = (event: MouseEvent) => {
      const drag = dragStateRef.current;
      if (!drag || drag.thumbTravel <= 0) return;

      const pointer =
        drag.axis === 'horizontal' ? event.clientX : event.clientY;
      const offset =
        drag.scrollStart +
        ((pointer - drag.pointerStart) / drag.thumbTravel) * drag.maxScroll;
      scrollToOffsetRef.current(drag.axis, offset);
      event.preventDefault();
    };

    const cancelDrag = () => {
      dragStateRef.current = null;
    };

    doc.addEventListener('mousemove', handleMouseMove);
    doc.addEventListener('mouseup', cancelDrag);
    doc.addEventListener('mouseleave', cancelDrag);
    win.addEventListener('blur', cancelDrag);
    return () => {
      doc.removeEventListener('mousemove', handleMouseMove);
      doc.removeEventListener('mouseup', cancelDrag);
      doc.removeEventListener('mouseleave', cancelDrag);
      win.removeEventListener('blur', cancelDrag);
      dragStateRef.current = null;
    };
  }, [isWebPlatform]);

  const verticalIndicator = useMemo(() => {
    if (bodyViewportHeight <= 0 || maxScrollY <= 0) return null;
    const thumbHeight = Math.min(
      bodyViewportHeight,
      Math.max(
        32,
        (bodyViewportHeight * bodyViewportHeight) / bodyContentHeight,
      ),
    );
    const thumbTravel = Math.max(bodyViewportHeight - thumbHeight, 0);
    return {
      thumbHeight,
      thumbTop: (verticalOffset / maxScrollY) * thumbTravel,
    };
  }, [bodyContentHeight, bodyViewportHeight, maxScrollY, verticalOffset]);

  const horizontalIndicator = useMemo(() => {
    if (viewportWidth <= 0 || maxScrollX <= 0) return null;
    const thumbWidth = Math.min(
      viewportWidth,
      Math.max(32, (viewportWidth * viewportWidth) / contentWidth),
    );
    const thumbTravel = Math.max(viewportWidth - thumbWidth, 0);
    return {
      thumbLeft: (horizontalOffset / maxScrollX) * thumbTravel,
      thumbWidth,
    };
  }, [contentWidth, horizontalOffset, maxScrollX, viewportWidth]);

  const getPointerCoordinate = (
    event: TableScrollMouseEvent,
    axis: TableScrollAxis,
  ) => {
    const nativeEvent = event.nativeEvent ?? event;
    const clientCoordinate =
      axis === 'horizontal'
        ? event.clientX ?? nativeEvent.clientX ?? nativeEvent.pageX
        : event.clientY ?? nativeEvent.clientY ?? nativeEvent.pageY;
    return Number.isFinite(clientCoordinate) ? clientCoordinate : 0;
  };

  const handleTrackMouseDown = (
    event: TableScrollMouseEvent,
    axis: TableScrollAxis,
    thumbSize: number,
    trackSize: number,
  ) => {
    const button = event.button ?? event.nativeEvent?.button ?? 0;
    if (!isWebPlatform || event.defaultPrevented || button !== 0) return;

    const nativeEvent = event.nativeEvent ?? event;
    const offsetCoordinate =
      axis === 'horizontal' ? nativeEvent.offsetX : nativeEvent.offsetY;
    const rect = event.currentTarget?.getBoundingClientRect?.();
    const pointerCoordinate = Number.isFinite(offsetCoordinate)
      ? offsetCoordinate
      : getPointerCoordinate(event, axis) -
        (axis === 'horizontal' ? rect?.left ?? 0 : rect?.top ?? 0);
    const thumbTravel = Math.max(trackSize - thumbSize, 0);
    const maxScroll = axis === 'horizontal' ? maxScrollX : maxScrollY;
    const centeredThumbPosition = Math.min(
      Math.max(pointerCoordinate - thumbSize / 2, 0),
      thumbTravel,
    );

    scrollToOffset(
      axis,
      thumbTravel > 0 ? (centeredThumbPosition / thumbTravel) * maxScroll : 0,
    );
    event.preventDefault?.();
  };

  const handleThumbMouseDown = (
    event: TableScrollMouseEvent,
    axis: TableScrollAxis,
    thumbTravel: number,
  ) => {
    const button = event.button ?? event.nativeEvent?.button ?? 0;
    if (!isWebPlatform || button !== 0) return;

    event.stopPropagation?.();
    event.preventDefault?.();
    dragStateRef.current = {
      axis,
      maxScroll: axis === 'horizontal' ? maxScrollX : maxScrollY,
      pointerStart: getPointerCoordinate(event, axis),
      scrollStart: axis === 'horizontal' ? horizontalOffset : verticalOffset,
      thumbTravel,
    };
  };

  const handleTrackWheel = (
    event: TableScrollMouseEvent,
    axis: TableScrollAxis,
  ) => {
    if (!isWebPlatform) return;

    const nativeEvent = event.nativeEvent ?? {};
    const deltaX = nativeEvent.deltaX ?? 0;
    const deltaY = nativeEvent.deltaY ?? 0;
    const delta = axis === 'vertical' ? deltaY : deltaX || deltaY;
    if (!delta) return;

    const offset =
      (axis === 'horizontal' ? horizontalOffset : verticalOffset) + delta;
    scrollToOffset(axis, offset);
    event.preventDefault?.();
  };

  return (
    <>
      {headerHeight > 0 ? (
        <View
          testID="users-table-header-scroll-gutter"
          pointerEvents="none"
          style={[styles.tableHeaderScrollGutter, { height: headerHeight }]}
        />
      ) : null}
      {verticalIndicator ? (
        <View
          testID="users-table-vertical-scrollbar"
          pointerEvents={isWebPlatform ? 'auto' : 'none'}
          onMouseDown={event =>
            handleTrackMouseDown(
              event,
              'vertical',
              verticalIndicator.thumbHeight,
              bodyViewportHeight,
            )
          }
          onWheel={event => handleTrackWheel(event, 'vertical')}
          style={[
            styles.tableVerticalScrollTrack,
            { top: headerHeight, height: bodyViewportHeight },
          ]}
        >
          <View
            testID="users-table-vertical-scrollbar-thumb"
            onMouseDown={event =>
              handleThumbMouseDown(
                event,
                'vertical',
                bodyViewportHeight - verticalIndicator.thumbHeight,
              )
            }
            style={[
              styles.tableVerticalScrollThumb,
              {
                height: verticalIndicator.thumbHeight,
                top: verticalIndicator.thumbTop,
              },
            ]}
          />
        </View>
      ) : null}
      {horizontalIndicator ? (
        <View
          testID="users-table-horizontal-scrollbar"
          pointerEvents={isWebPlatform ? 'auto' : 'none'}
          onMouseDown={event =>
            handleTrackMouseDown(
              event,
              'horizontal',
              horizontalIndicator.thumbWidth,
              viewportWidth,
            )
          }
          onWheel={event => handleTrackWheel(event, 'horizontal')}
          style={[styles.tableHorizontalScrollTrack, { width: viewportWidth }]}
        >
          <View
            testID="users-table-horizontal-scrollbar-thumb"
            onMouseDown={event =>
              handleThumbMouseDown(
                event,
                'horizontal',
                viewportWidth - horizontalIndicator.thumbWidth,
              )
            }
            style={[
              styles.tableHorizontalScrollThumb,
              {
                left: horizontalIndicator.thumbLeft,
                width: horizontalIndicator.thumbWidth,
              },
            ]}
          />
        </View>
      ) : null}
    </>
  );
});

// Time range options for last login filter
const LAST_LOGIN_OPTIONS: { value: LastLoginTimeRange | ''; label: string }[] =
  [
    { value: '', label: 'Any time' },
    { value: 'hour', label: 'Last 1 hour' },
    { value: 'day', label: 'Last 1 day' },
    { value: 'week', label: 'Last week' },
    { value: 'month', label: 'Last month' },
    { value: '90days', label: 'Last 90 days' },
    { value: 'year', label: 'Last year' },
  ];

export const RoleManagement: React.FC<RoleManagementProps> = ({
  navigate,
  currentPath,
}) => {
  const { user } = useAuth();
  const { width: windowWidth } = useWindowDimensions();
  const {
    startImpersonation,
    isImpersonating,
    isLoading: isImpersonationLoading,
  } = useImpersonation();

  // Tab state
  const [activeTab, setActiveTab] = useState<'roles' | 'sponsors' | 'groups'>(
    'roles',
  );

  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [editingUser, setEditingUser] = useState<EditingState | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Default Sponsors modal state
  const [activeModal, setActiveModal] = useState<
    'nam' | 'emea' | 'apac' | null
  >(null);
  const [modalSearchText, setModalSearchText] = useState('');
  const [modalSelectedUsers, setModalSelectedUsers] = useState<Set<number>>(
    new Set(),
  );

  // Pending selections for each region (until saved)
  const [pendingSponsorsNAM, setPendingSponsorsNAM] = useState<Set<number>>(
    new Set(),
  );
  const [pendingSponsorsEMEA, setPendingSponsorsEMEA] = useState<Set<number>>(
    new Set(),
  );
  const [pendingSponsorsAPAC, setPendingSponsorsAPAC] = useState<Set<number>>(
    new Set(),
  );

  // Pending removals for each region (until saved)
  const [pendingRemovalsNAM, setPendingRemovalsNAM] = useState<Set<number>>(
    new Set(),
  );
  const [pendingRemovalsEMEA, setPendingRemovalsEMEA] = useState<Set<number>>(
    new Set(),
  );
  const [pendingRemovalsAPAC, setPendingRemovalsAPAC] = useState<Set<number>>(
    new Set(),
  );

  // Saving state for Default Sponsors
  const [isSavingSponsors, setIsSavingSponsors] = useState(false);

  // Impersonation state
  const [selectedUserForImpersonation, setSelectedUserForImpersonation] =
    useState<UserWithRoles | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [impersonationError, setImpersonationError] = useState<string | null>(
    null,
  );
  const [selectedImpersonationMode, setSelectedImpersonationMode] =
    useState<ImpersonationMode | null>(null);

  // Check if user can manage roles (Role Manager or IT Administrator)
  const isItAdministrator =
    user?.roles?.includes(AppRole.IT_ADMINISTRATOR) === true;
  const canManageRoles =
    user?.roles?.includes(AppRole.ROLE_MANAGER) === true || isItAdministrator;
  const isOwnRow = (userRow: UserWithRoles) =>
    userRow.userId === Number(user?.id);
  const isRoleLockedForCaller = (roleName: string) =>
    PRIVILEGED_ROLE_NAMES.has(roleName) && !isItAdministrator;

  // Check if user can manage Default Sponsors (IT Administrator only)
  const canManageDefaultSponsors = roleService.canManageDefaultSponsors(user);
  const visibleActiveTab =
    !canManageDefaultSponsors &&
    (activeTab === 'sponsors' || activeTab === 'groups')
      ? 'roles'
      : activeTab;

  // Check impersonation role capabilities
  const hasFullAccess =
    user?.roles?.includes(AppRole.IMPERSONATOR_FULL_ACCESS) ?? false;
  const hasReadOnly =
    user?.roles?.includes(AppRole.IMPERSONATOR_READ_ONLY) ?? false;
  const hasImpersonatorRole = hasFullAccess || hasReadOnly;
  const hasBothImpersonatorRoles = hasFullAccess && hasReadOnly;

  // Filter states
  const [externalOnly, setExternalOnly] = useState(false);
  const [freezeUserColumn, setFreezeUserColumn] = useState(false);
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [excludeRoleIds, setExcludeRoleIds] = useState<number[]>([]);
  const [lastLoginWithin, setLastLoginWithin] = useState<
    LastLoginTimeRange | ''
  >('');
  const [showRoleFilterDropdown, setShowRoleFilterDropdown] = useState(false);
  const [roleFilterMode, setRoleFilterMode] = useState<'include' | 'exclude'>(
    'include',
  );
  const [tableHeaderHeight, setTableHeaderHeight] = useState(0);
  const [tableBodyViewportHeight, setTableBodyViewportHeight] = useState(0);
  const [tableBodyContentHeight, setTableBodyContentHeight] = useState(0);
  const [tableViewportWidth, setTableViewportWidth] = useState(0);
  const [tableContentWidth, setTableContentWidth] = useState(0);
  const tableScrollIndicatorRef = useRef<TableScrollIndicatorHandle>(null);
  const tableHorizontalScrollRef = useRef<ScrollView>(null);
  const tableVerticalScrollRef = useRef<ScrollView>(null);
  const tableScrollportRef = useRef<View>(null);
  const tableHeaderHeightRef = useRef(0);
  const tableViewportHeightRef = useRef(0);
  const tableHeightRef = useRef(0);

  // Ref for role filter dropdown to detect outside clicks
  const roleFilterDropdownRef = useRef<View>(null);

  // Close dropdown when clicking outside (web only)
  useEffect(() => {
    if (Platform.OS !== 'web' || !showRoleFilterDropdown) return;

    // Use globalThis to access document in a TypeScript-safe way for web
    const doc = (globalThis as any).document;
    if (!doc) return;

    const handleClickOutside = (event: Event) => {
      const target = event.target as HTMLElement;
      // Check if click is outside the dropdown container
      const dropdownElement = doc.querySelector('[data-role-filter-dropdown]');
      if (dropdownElement && !dropdownElement.contains(target)) {
        setShowRoleFilterDropdown(false);
      }
    };

    // Add listener with a small delay to avoid immediate close from the toggle click
    const timeoutId = setTimeout(() => {
      doc.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      doc.removeEventListener('click', handleClickOutside);
    };
  }, [showRoleFilterDropdown]);

  // Keyboard shortcuts (Esc to close modal, Ctrl/Cmd+S to save)
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const doc = (globalThis as any).document;
    if (!doc) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Esc to close dropdowns/modals
      if (event.key === 'Escape') {
        if (showRoleFilterDropdown) {
          setShowRoleFilterDropdown(false);
        } else if (activeModal) {
          setActiveModal(null);
        }
      }

      // Ctrl/Cmd+S to save changes (only when on Default Sponsors tab with unsaved changes)
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key === 's' &&
        activeTab === 'sponsors'
      ) {
        event.preventDefault();
        if (hasUnsavedChanges && !isSavingSponsors) {
          // Find first region with unsaved changes and save it
          if (hasRegionUnsavedChanges('nam')) {
            handleSaveSponsorChanges('nam');
          } else if (hasRegionUnsavedChanges('emea')) {
            handleSaveSponsorChanges('emea');
          } else if (hasRegionUnsavedChanges('apac')) {
            handleSaveSponsorChanges('apac');
          }
        }
      }
    };

    doc.addEventListener('keydown', handleKeyDown);
    return () => {
      doc.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleSaveSponsorChanges/hasRegionUnsavedChanges intentionally omitted to avoid re-registering listener on every save cycle
  }, [
    activeModal,
    activeTab,
    hasUnsavedChanges,
    isSavingSponsors,
    showRoleFilterDropdown,
  ]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Fetch data with filters
  const {
    data: usersData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useUsers({
    page,
    pageSize,
    search: debouncedSearch || undefined,
    externalOnly: externalOnly || undefined,
    roleIds: selectedRoleIds.length > 0 ? selectedRoleIds : undefined,
    excludeRoleIds: excludeRoleIds.length > 0 ? excludeRoleIds : undefined,
    lastLoginWithin: lastLoginWithin || undefined,
  });

  const { data: roles } = useRoles();
  const assignRole = useAssignRole();
  const removeRole = useRemoveRole();

  // Check if user can access role management (IT Admin or dev access)
  const canAccessRoleManagement = roleService.canAccessRoleManagement(user);

  // Create role map for easy lookup (must be before conditional return)
  const roleMap = useMemo(() => {
    if (!roles) return new Map();
    return new Map(roles.map(role => [role.name, role]));
  }, [roles]);

  const sortedRoles = useMemo(() => {
    if (!roles) return undefined;
    // Filter out Default Sponsor roles — they have their own tab
    const DEFAULT_SPONSOR_ROLES = [
      AppRole.DEFAULT_SPONSOR_NAM,
      AppRole.DEFAULT_SPONSOR_EMEA,
      AppRole.DEFAULT_SPONSOR_APAC,
    ];
    return [...roles]
      .filter(r => !DEFAULT_SPONSOR_ROLES.includes(r.name as AppRole))
      .filter(r => !isLvoPairedRoleName(r.name))
      .sort((a, b) => {
        const aIndex = ROLE_DISPLAY_ORDER.indexOf(a.name);
        const bIndex = ROLE_DISPLAY_ORDER.indexOf(b.name);
        const aOrder = aIndex === -1 ? ROLE_DISPLAY_ORDER.length : aIndex;
        const bOrder = bIndex === -1 ? ROLE_DISPLAY_ORDER.length : bIndex;
        return aOrder - bOrder;
      });
  }, [roles]);

  const tableRoleColumns = useMemo((): UsersTableRoleColumn[] | undefined => {
    if (!sortedRoles) return undefined;
    const columns: UsersTableRoleColumn[] = [];
    for (const role of sortedRoles) {
      columns.push({ type: 'role', role });
      if (role.name === AppRole.PARTNER_CONTRIBUTOR) {
        columns.push({ type: 'lvo' });
      }
    }
    return columns;
  }, [sortedRoles]);

  // Calculate table min width from the same widths used by header/body cells
  const tableMinWidth = useMemo(() => {
    let width =
      USER_COLUMN_WIDTH +
      DATE_COLUMN_WIDTH +
      DATE_COLUMN_WIDTH +
      TABLE_HORIZONTAL_PADDING;
    if (canManageRoles || hasImpersonatorRole) width += ACTION_COLUMN_WIDTH;
    if (canManageRoles && tableRoleColumns) {
      width += tableRoleColumns.length * ROLE_COLUMN_WIDTH;
    }
    return width;
  }, [canManageRoles, hasImpersonatorRole, tableRoleColumns]);

  // Redirect if not authorized for role management
  useEffect(() => {
    if (user && !canAccessRoleManagement) {
      navigate('/unauthorized');
    }
  }, [user, canAccessRoleManagement, navigate]);

  // Redirect to login on 401 authentication errors
  useEffect(() => {
    if (
      error &&
      ((error as any).statusCode === 401 || (error as any).status === 401)
    ) {
      console.log('Authentication required - redirecting to login');
      navigate('/');
    }
  }, [error, navigate]);

  // Reset restricted tabs when the effective user loses administrator access.
  useEffect(() => {
    if (
      (activeTab === 'sponsors' || activeTab === 'groups') &&
      !canManageDefaultSponsors
    ) {
      setActiveTab('roles');
    }
  }, [activeTab, canManageDefaultSponsors]);

  // Find role IDs for Default Sponsors (uses `roles` from useRoles above)
  const allRoles = roles || [];
  const defaultSponsorNAMRole = allRoles.find(
    r => r.name === AppRole.DEFAULT_SPONSOR_NAM,
  );
  const defaultSponsorEMEARole = allRoles.find(
    r => r.name === AppRole.DEFAULT_SPONSOR_EMEA,
  );
  const defaultSponsorAPACRole = allRoles.find(
    r => r.name === AppRole.DEFAULT_SPONSOR_APAC,
  );

  // Fetch users with Default Sponsor roles (only when user has IT Admin access)
  const { data: namSponsorsData, isLoading: isLoadingNAM } = useUsers(
    {
      page: 1,
      pageSize: 100,
      roleIds: defaultSponsorNAMRole ? [defaultSponsorNAMRole.id] : [],
    },
    { enabled: canManageDefaultSponsors },
  );

  const { data: emeaSponsorsData, isLoading: isLoadingEMEA } = useUsers(
    {
      page: 1,
      pageSize: 100,
      roleIds: defaultSponsorEMEARole ? [defaultSponsorEMEARole.id] : [],
    },
    { enabled: canManageDefaultSponsors },
  );

  const { data: apacSponsorsData, isLoading: isLoadingAPAC } = useUsers(
    {
      page: 1,
      pageSize: 100,
      roleIds: defaultSponsorAPACRole ? [defaultSponsorAPACRole.id] : [],
    },
    { enabled: canManageDefaultSponsors },
  );

  const namSponsors = namSponsorsData?.users || [];
  const emeaSponsors = emeaSponsorsData?.users || [];
  const apacSponsors = apacSponsorsData?.users || [];

  // Calculate if there are unsaved changes (any region)
  const hasUnsavedChanges = useMemo(() => {
    const hasPendingAdditions =
      pendingSponsorsNAM.size > 0 ||
      pendingSponsorsEMEA.size > 0 ||
      pendingSponsorsAPAC.size > 0;

    const hasPendingRemovals =
      pendingRemovalsNAM.size > 0 ||
      pendingRemovalsEMEA.size > 0 ||
      pendingRemovalsAPAC.size > 0;

    return hasPendingAdditions || hasPendingRemovals;
  }, [
    pendingSponsorsNAM,
    pendingSponsorsEMEA,
    pendingSponsorsAPAC,
    pendingRemovalsNAM,
    pendingRemovalsEMEA,
    pendingRemovalsAPAC,
  ]);

  // Don't render anything if not authorized
  if (!canAccessRoleManagement) {
    return null;
  }

  // Start editing a user row
  const handleEditClick = (userRow: UserWithRoles) => {
    const currentRoleNames = new Set(userRow.roles.map(r => r.name));
    setEditingUser({
      userId: userRow.userId,
      selectedRoles: currentRoleNames,
    });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingUser(null);
  };

  // Toggle role checkbox while editing
  const handleRoleToggleWhileEditing = (roleName: string) => {
    if (!editingUser) return;

    const newSelectedRoles = new Set(editingUser.selectedRoles);
    if (newSelectedRoles.has(roleName)) {
      newSelectedRoles.delete(roleName);
      removePairedLvoWhenChannelRemoved(newSelectedRoles, roleName);
    } else {
      newSelectedRoles.add(roleName);
    }

    setEditingUser({
      ...editingUser,
      selectedRoles: newSelectedRoles,
    });
  };

  const handleLvoToggleWhileEditing = () => {
    if (!editingUser) return;

    const newSelectedRoles = new Set(editingUser.selectedRoles);
    toggleLvoColumnInSelection(newSelectedRoles);

    setEditingUser({
      ...editingUser,
      selectedRoles: newSelectedRoles,
    });
  };

  // Save changes
  const handleSaveChanges = async (userRow: UserWithRoles) => {
    if (!editingUser) return;

    setIsSaving(true);
    try {
      const currentRoleNames = new Set(userRow.roles.map(r => r.name));
      const newRoleNames = editingUser.selectedRoles;

      // Find roles to add (in new but not in current)
      const rolesToAdd: string[] = [];
      newRoleNames.forEach(roleName => {
        if (!currentRoleNames.has(roleName)) {
          rolesToAdd.push(roleName);
        }
      });

      // Find roles to remove (in current but not in new)
      const rolesToRemove: string[] = [];
      currentRoleNames.forEach(roleName => {
        if (!newRoleNames.has(roleName)) {
          rolesToRemove.push(roleName);
        }
      });

      // Execute role changes
      for (const roleName of rolesToAdd) {
        const role = roleMap.get(roleName);
        if (role) {
          await assignRole.mutateAsync({
            userId: userRow.userId,
            roleId: role.id,
          });
        }
      }

      for (const roleName of rolesToRemove) {
        const role = roleMap.get(roleName);
        if (role) {
          await removeRole.mutateAsync({
            userId: userRow.userId,
            roleId: role.id,
          });
        }
      }

      // Exit edit mode
      setEditingUser(null);
    } catch (err) {
      console.error('Failed to save role changes:', err);
      alert(
        err instanceof Error && err.message
          ? err.message
          : 'Failed to save changes. Please try again.',
      );
      // Changes before the failure were saved; show the row as it now is.
      refetch();
    } finally {
      setIsSaving(false);
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  // Impersonation handlers
  const handleLoginAsClick = (userRow: UserWithRoles) => {
    setSelectedUserForImpersonation(userRow);
    setImpersonationError(null);
    // Set default mode based on user's impersonation roles
    if (hasBothImpersonatorRoles) {
      setSelectedImpersonationMode('read_only'); // Safe default
    } else if (hasFullAccess) {
      setSelectedImpersonationMode('full_access');
    } else {
      setSelectedImpersonationMode('read_only');
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmImpersonation = async () => {
    if (!selectedUserForImpersonation) return;
    setImpersonationError(null);
    try {
      await startImpersonation(
        selectedUserForImpersonation.userId,
        selectedImpersonationMode ?? undefined,
      );
      setShowConfirmDialog(false);
      setSelectedUserForImpersonation(null);
      setSelectedImpersonationMode(null);
      navigate('/view-requests');
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to start impersonation session';
      setImpersonationError(message);
    }
  };

  const handleCancelImpersonation = () => {
    setShowConfirmDialog(false);
    setSelectedUserForImpersonation(null);
    setImpersonationError(null);
    setSelectedImpersonationMode(null);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  // Filter helpers
  const handleRoleFilterToggle = (roleId: number) => {
    if (roleFilterMode === 'include') {
      setSelectedRoleIds(prev =>
        prev.includes(roleId)
          ? prev.filter(id => id !== roleId)
          : [...prev, roleId],
      );
    } else {
      setExcludeRoleIds(prev =>
        prev.includes(roleId)
          ? prev.filter(id => id !== roleId)
          : [...prev, roleId],
      );
    }
    setPage(1);
  };

  const clearAllFilters = () => {
    setExternalOnly(false);
    setSelectedRoleIds([]);
    setExcludeRoleIds([]);
    setLastLoginWithin('');
    setPage(1);
  };

  const hasActiveFilters =
    externalOnly ||
    selectedRoleIds.length > 0 ||
    excludeRoleIds.length > 0 ||
    lastLoginWithin !== '';

  const activeRoleIds =
    roleFilterMode === 'include' ? selectedRoleIds : excludeRoleIds;

  const users = usersData?.users || [];
  const pagination = usersData?.pagination;
  const isNativeIdentityFreeze = freezeUserColumn && Platform.OS !== 'web';
  const isWebUsersTable = Platform.OS === 'web';

  const scrollWebTable = (axis: 'horizontal' | 'vertical', offset: number) => {
    const node =
      tableScrollportRef.current as unknown as WebTableScrollportNode | null;
    if (!node) {
      return;
    }
    if (axis === 'horizontal') {
      node.scrollTo?.({ x: offset, animated: false });
      node.scrollLeft = offset;
      return;
    }
    node.scrollTo?.({ y: offset, animated: false });
    node.scrollTop = offset;
  };
  const totalPages = pagination?.totalPages || 0;
  const hasNextPage = page < totalPages;
  const hasPrevPage = page > 1;

  // Handle opening Add User modal for a region
  const handleOpenAddUserModal = (region: 'nam' | 'emea' | 'apac') => {
    // Initialize modal with current selections
    const currentSelections = getCurrentSelections(region);
    setModalSelectedUsers(new Set(currentSelections));
    setModalSearchText('');
    setActiveModal(region);
  };

  // Handle user selection from modal (receives user IDs)
  const handleUserSelection = (selectedUserIds: number[]) => {
    if (!activeModal) return;

    // Update pending state based on active modal
    switch (activeModal) {
      case 'nam':
        setPendingSponsorsNAM(new Set(selectedUserIds));
        break;
      case 'emea':
        setPendingSponsorsEMEA(new Set(selectedUserIds));
        break;
      case 'apac':
        setPendingSponsorsAPAC(new Set(selectedUserIds));
        break;
    }

    setActiveModal(null);
  };

  // Get current selections including pending additions for a region (as user IDs)
  const getCurrentSelections = (region: 'nam' | 'emea' | 'apac'): number[] => {
    const currentSponsors =
      region === 'nam'
        ? namSponsors
        : region === 'emea'
        ? emeaSponsors
        : apacSponsors;
    const pendingSet =
      region === 'nam'
        ? pendingSponsorsNAM
        : region === 'emea'
        ? pendingSponsorsEMEA
        : pendingSponsorsAPAC;

    // Get IDs from current sponsors
    const currentIds = currentSponsors.map(s => s.userId);

    // Get IDs from pending additions
    const pendingIds = Array.from(pendingSet);

    // Combine and deduplicate
    return Array.from(new Set([...currentIds, ...pendingIds]));
  };

  // Get modal type based on active region
  const getModalType = ():
    | 'default_sponsor_nam'
    | 'default_sponsor_emea'
    | 'default_sponsor_apac'
    | null => {
    if (!activeModal) return null;
    return activeModal === 'nam'
      ? 'default_sponsor_nam'
      : activeModal === 'emea'
      ? 'default_sponsor_emea'
      : 'default_sponsor_apac';
  };

  // Get modal title based on active region
  const getModalTitle = (): string => {
    if (!activeModal) return '';
    return activeModal === 'nam'
      ? 'Add Default Sponsor NAM'
      : activeModal === 'emea'
      ? 'Add Default Sponsor EMEA'
      : 'Add Default Sponsor APAC';
  };

  // Get all sponsors for a region (current + pending additions, keeping pending removals visible)
  const getAllSponsors = (region: 'nam' | 'emea' | 'apac'): UserWithRoles[] => {
    const currentSponsors =
      region === 'nam'
        ? namSponsors
        : region === 'emea'
        ? emeaSponsors
        : apacSponsors;
    const pendingSet =
      region === 'nam'
        ? pendingSponsorsNAM
        : region === 'emea'
        ? pendingSponsorsEMEA
        : pendingSponsorsAPAC;
    const pendingRemovalSet =
      region === 'nam'
        ? pendingRemovalsNAM
        : region === 'emea'
        ? pendingRemovalsEMEA
        : pendingRemovalsAPAC;

    // Get pending users (newly added but not yet saved)
    const allUsersData = usersData?.users || [];
    const pendingUsers = Array.from(pendingSet)
      .map(userId => allUsersData.find(u => u.userId === userId))
      .filter((user): user is UserWithRoles => user !== undefined)
      // Only include pending users that are NOT marked for removal
      .filter(user => !pendingRemovalSet.has(user.userId));

    // Combine current and pending, removing duplicates
    const sponsorMap = new Map<number, UserWithRoles>();
    currentSponsors.forEach(s => sponsorMap.set(s.userId, s));
    pendingUsers.forEach(u => sponsorMap.set(u.userId, u));

    // Return all sponsors (including those marked for removal - they'll be styled differently in the UI)
    return Array.from(sponsorMap.values());
  };

  // Check if a user is pending (newly added)
  const isPendingSponsor = (
    userId: number,
    region: 'nam' | 'emea' | 'apac',
  ): boolean => {
    const currentSponsors =
      region === 'nam'
        ? namSponsors
        : region === 'emea'
        ? emeaSponsors
        : apacSponsors;
    const pendingSet =
      region === 'nam'
        ? pendingSponsorsNAM
        : region === 'emea'
        ? pendingSponsorsEMEA
        : pendingSponsorsAPAC;

    // A user is pending if they're in the pending set but not in the current sponsors
    return (
      pendingSet.has(userId) && !currentSponsors.some(s => s.userId === userId)
    );
  };

  // Check if a user is pending removal
  const isPendingRemoval = (
    userId: number,
    region: 'nam' | 'emea' | 'apac',
  ): boolean => {
    const pendingRemovalSet =
      region === 'nam'
        ? pendingRemovalsNAM
        : region === 'emea'
        ? pendingRemovalsEMEA
        : pendingRemovalsAPAC;
    return pendingRemovalSet.has(userId);
  };

  // Handle removing a user (mark for pending removal)
  const handleRemoveUser = (
    userId: number,
    region: 'nam' | 'emea' | 'apac',
  ) => {
    const currentSponsors =
      region === 'nam'
        ? namSponsors
        : region === 'emea'
        ? emeaSponsors
        : apacSponsors;
    const pendingSet =
      region === 'nam'
        ? pendingSponsorsNAM
        : region === 'emea'
        ? pendingSponsorsEMEA
        : pendingSponsorsAPAC;
    const setPending =
      region === 'nam'
        ? setPendingSponsorsNAM
        : region === 'emea'
        ? setPendingSponsorsEMEA
        : setPendingSponsorsAPAC;
    const pendingRemovalSet =
      region === 'nam'
        ? pendingRemovalsNAM
        : region === 'emea'
        ? pendingRemovalsEMEA
        : pendingRemovalsAPAC;
    const setPendingRemoval =
      region === 'nam'
        ? setPendingRemovalsNAM
        : region === 'emea'
        ? setPendingRemovalsEMEA
        : setPendingRemovalsAPAC;

    // Check if this is a pending addition (not yet saved)
    const isPendingAddition =
      pendingSet.has(userId) && !currentSponsors.some(s => s.userId === userId);

    if (isPendingAddition) {
      // Remove from pending additions
      const newPendingSet = new Set(pendingSet);
      newPendingSet.delete(userId);
      setPending(newPendingSet);
    } else {
      // Add to pending removals
      const newPendingRemovalSet = new Set(pendingRemovalSet);
      newPendingRemovalSet.add(userId);
      setPendingRemoval(newPendingRemovalSet);
    }
  };

  // Check if a specific region has unsaved changes
  const hasRegionUnsavedChanges = (
    region: 'nam' | 'emea' | 'apac',
  ): boolean => {
    const pendingAdditions =
      region === 'nam'
        ? pendingSponsorsNAM
        : region === 'emea'
        ? pendingSponsorsEMEA
        : pendingSponsorsAPAC;
    const pendingRemovals =
      region === 'nam'
        ? pendingRemovalsNAM
        : region === 'emea'
        ? pendingRemovalsEMEA
        : pendingRemovalsAPAC;
    return pendingAdditions.size > 0 || pendingRemovals.size > 0;
  };

  // Handle Cancel - revert changes for a specific region
  const handleCancelChanges = (region?: 'nam' | 'emea' | 'apac') => {
    if (!region) {
      // Clear all if no region specified (for backwards compatibility)
      setPendingSponsorsNAM(new Set());
      setPendingSponsorsEMEA(new Set());
      setPendingSponsorsAPAC(new Set());
      setPendingRemovalsNAM(new Set());
      setPendingRemovalsEMEA(new Set());
      setPendingRemovalsAPAC(new Set());
      return;
    }

    // Clear only the specified region
    switch (region) {
      case 'nam':
        setPendingSponsorsNAM(new Set());
        setPendingRemovalsNAM(new Set());
        break;
      case 'emea':
        setPendingSponsorsEMEA(new Set());
        setPendingRemovalsEMEA(new Set());
        break;
      case 'apac':
        setPendingSponsorsAPAC(new Set());
        setPendingRemovalsAPAC(new Set());
        break;
    }
  };

  // Handle Save - persist changes to backend for a specific region
  const handleSaveSponsorChanges = async (region: 'nam' | 'emea' | 'apac') => {
    const loadingToast = showToast.loading('Saving changes...');
    setIsSavingSponsors(true);
    try {
      const errors: string[] = [];

      // Get the data for the specific region
      const pendingAdditions =
        region === 'nam'
          ? pendingSponsorsNAM
          : region === 'emea'
          ? pendingSponsorsEMEA
          : pendingSponsorsAPAC;
      const pendingRemovals =
        region === 'nam'
          ? pendingRemovalsNAM
          : region === 'emea'
          ? pendingRemovalsEMEA
          : pendingRemovalsAPAC;
      const currentSponsors =
        region === 'nam'
          ? namSponsors
          : region === 'emea'
          ? emeaSponsors
          : apacSponsors;

      // Process only this region
      await processSponsorChanges(
        region,
        pendingAdditions,
        pendingRemovals,
        currentSponsors,
        errors,
      );

      showToast.dismiss(loadingToast);

      if (errors.length > 0) {
        showToast.error(`Some changes failed to save:\n${errors.join('\n')}`);
      } else {
        // Success - clear pending state for this region only
        handleCancelChanges(region);
        showToast.success('Changes saved successfully');
      }
    } catch (err) {
      showToast.dismiss(loadingToast);
      console.error('Failed to save sponsor changes:', err);
      showToast.error('Failed to save changes. Please try again.');
    } finally {
      setIsSavingSponsors(false);
    }
  };

  // Process sponsor changes for a specific region
  const processSponsorChanges = async (
    region: 'nam' | 'emea' | 'apac',
    pendingAdditions: Set<number>,
    pendingRemovals: Set<number>,
    currentSponsors: UserWithRoles[],
    errors: string[],
  ) => {
    const roleMap = new Map([
      ['nam', AppRole.DEFAULT_SPONSOR_NAM],
      ['emea', AppRole.DEFAULT_SPONSOR_EMEA],
      ['apac', AppRole.DEFAULT_SPONSOR_APAC],
    ]);

    const roleName = roleMap.get(region);
    if (!roleName) return;

    const role = allRoles.find(r => r.name === roleName);
    if (!role) return;

    // Process additions
    for (const userId of Array.from(pendingAdditions)) {
      // Only add if not already a current sponsor
      if (!currentSponsors.some(s => s.userId === userId)) {
        try {
          await assignRole.mutateAsync({ userId, roleId: role.id });
        } catch (err) {
          console.error(`Failed to assign ${roleName} to user ${userId}:`, err);
          errors.push(`Failed to add user ${userId} as ${roleName}`);
        }
      }
    }

    // Process removals
    for (const userId of Array.from(pendingRemovals)) {
      try {
        await removeRole.mutateAsync({ userId, roleId: role.id });
      } catch (err) {
        console.error(`Failed to remove ${roleName} from user ${userId}:`, err);
        errors.push(`Failed to remove user ${userId} from ${roleName}`);
      }
    }
  };

  // Default Sponsors Tab Component
  const renderDefaultSponsorsTab = () => {
    const isDesktop = windowWidth >= 1024;

    const renderSponsorContainer = (
      title: string,
      region: 'nam' | 'emea' | 'apac',
      isLoading: boolean,
    ) => {
      const allSponsors = getAllSponsors(region);

      return (
        <View
          style={[
            styles.sponsorContainer,
            !isDesktop && styles.sponsorContainerMobile,
          ]}
        >
          <Text style={styles.sponsorContainerHeader}>{title}</Text>

          <ScrollView
            style={styles.sponsorList}
            contentContainerStyle={styles.sponsorListContent}
          >
            {isLoading ? (
              <View style={styles.sponsorLoadingContainer}>
                <ActivityIndicator size="small" color="#FF6B35" />
                <Text style={styles.sponsorLoadingText}>Loading...</Text>
              </View>
            ) : allSponsors.length === 0 ? (
              <View style={styles.sponsorEmptyContainer}>
                <Text style={styles.sponsorEmptyText}>
                  No default sponsors assigned
                </Text>
              </View>
            ) : (
              allSponsors.map(sponsor => {
                const isPending = isPendingSponsor(sponsor.userId, region);
                const isMarkedForRemoval = isPendingRemoval(
                  sponsor.userId,
                  region,
                );

                return (
                  <View
                    key={sponsor.userId}
                    style={[
                      styles.sponsorItem,
                      isPending && styles.sponsorItemPending,
                      isMarkedForRemoval && styles.sponsorItemRemoval,
                    ]}
                  >
                    <View style={styles.sponsorInfo}>
                      <Text
                        style={[
                          styles.sponsorName,
                          isMarkedForRemoval && styles.sponsorNameRemoval,
                        ]}
                      >
                        {sponsor.firstName || sponsor.lastName
                          ? `${sponsor.firstName || ''} ${
                              sponsor.lastName || ''
                            }`.trim()
                          : sponsor.email}
                      </Text>
                      {(sponsor.firstName || sponsor.lastName) && (
                        <Text
                          style={[
                            styles.sponsorEmail,
                            isMarkedForRemoval && styles.sponsorEmailRemoval,
                          ]}
                        >
                          {sponsor.email}
                        </Text>
                      )}
                      {isPending && (
                        <Text style={styles.sponsorPendingLabel}>
                          (Pending)
                        </Text>
                      )}
                      {isMarkedForRemoval && (
                        <Text style={styles.sponsorRemovalLabel}>
                          (Marked for removal)
                        </Text>
                      )}
                    </View>
                    <TouchableOpacity
                      style={styles.removeButton}
                      onPress={() => handleRemoveUser(sponsor.userId, region)}
                      accessibilityLabel={`Remove ${
                        sponsor.firstName || sponsor.lastName
                          ? `${sponsor.firstName || ''} ${
                              sponsor.lastName || ''
                            }`.trim()
                          : sponsor.email
                      }`}
                      accessibilityRole="button"
                      accessibilityHint="Marks user for removal"
                    >
                      <X size={18} color="#999" />
                    </TouchableOpacity>
                  </View>
                );
              })
            )}
          </ScrollView>

          <View
            style={[
              styles.sponsorFooter,
              !isDesktop && styles.sponsorFooterMobile,
            ]}
          >
            <TouchableOpacity
              style={[
                styles.addUserButton,
                !isDesktop && styles.addUserButtonMobile,
              ]}
              onPress={() => handleOpenAddUserModal(region)}
              disabled={isSavingSponsors}
              accessibilityLabel={`Add user to Default Sponsor ${title
                .split(' ')
                .pop()}`}
              accessibilityRole="button"
              accessibilityHint="Opens modal to select users"
            >
              <UserPlus size={16} color="#FF6B35" />
              <Text style={styles.addUserButtonText}>Add User</Text>
            </TouchableOpacity>

            {hasRegionUnsavedChanges(region) && (
              <View
                style={[
                  styles.sponsorFooterButtons,
                  !isDesktop && styles.sponsorFooterButtonsMobile,
                ]}
              >
                <TouchableOpacity
                  style={[
                    styles.cancelChangesButton,
                    !isDesktop && styles.cancelChangesButtonMobile,
                  ]}
                  onPress={() => handleCancelChanges(region)}
                  disabled={isSavingSponsors}
                  accessibilityLabel="Cancel changes"
                  accessibilityRole="button"
                  accessibilityHint="Discards unsaved changes for this region"
                >
                  <Text style={styles.cancelChangesButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.saveChangesButton,
                    !isDesktop && styles.saveChangesButtonMobile,
                    isSavingSponsors && styles.saveChangesButtonDisabled,
                  ]}
                  onPress={() => handleSaveSponsorChanges(region)}
                  disabled={isSavingSponsors}
                  accessibilityLabel="Save changes"
                  accessibilityRole="button"
                  accessibilityHint="Saves all pending changes for this region"
                  accessibilityState={{ disabled: isSavingSponsors }}
                >
                  {isSavingSponsors ? (
                    <ActivityIndicator size="small" color="#fff" />
                  ) : (
                    <>
                      <Save size={16} color="#fff" />
                      <Text style={styles.saveChangesButtonText}>
                        {isDesktop ? 'Save Changes' : 'Save'}
                      </Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>
      );
    };

    return (
      <ScrollView
        style={styles.defaultSponsorsContent}
        contentContainerStyle={styles.defaultSponsorsContentContainer}
      >
        <View
          style={[styles.sponsorGrid, !isDesktop && styles.sponsorGridMobile]}
        >
          {renderSponsorContainer('Default Sponsor NAM', 'nam', isLoadingNAM)}
          {renderSponsorContainer(
            'Default Sponsor EMEA',
            'emea',
            isLoadingEMEA,
          )}
          {renderSponsorContainer(
            'Default Sponsor APAC',
            'apac',
            isLoadingAPAC,
          )}
        </View>
      </ScrollView>
    );
  };

  // Shared role filter dropdown content (used by both web dropdown and native modal)
  const renderRoleFilterContent = () => (
    <>
      {/* Mode Toggle */}
      <View style={styles.roleFilterModeContainer}>
        <TouchableOpacity
          style={[
            styles.roleFilterModeButton,
            roleFilterMode === 'include' && styles.roleFilterModeButtonActive,
          ]}
          onPress={() => {
            setRoleFilterMode('include');
            setExcludeRoleIds([]);
          }}
          accessibilityRole="button"
          accessibilityLabel="Include roles"
          accessibilityState={{ selected: roleFilterMode === 'include' }}
        >
          <Text
            style={[
              styles.roleFilterModeText,
              roleFilterMode === 'include' && styles.roleFilterModeTextActive,
            ]}
          >
            Include
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.roleFilterModeButton,
            roleFilterMode === 'exclude' && styles.roleFilterModeButtonActive,
          ]}
          onPress={() => {
            setRoleFilterMode('exclude');
            setSelectedRoleIds([]);
          }}
          accessibilityRole="button"
          accessibilityLabel="Exclude roles"
          accessibilityState={{ selected: roleFilterMode === 'exclude' }}
        >
          <Text
            style={[
              styles.roleFilterModeText,
              roleFilterMode === 'exclude' && styles.roleFilterModeTextActive,
            ]}
          >
            Exclude
          </Text>
        </TouchableOpacity>
      </View>

      {/* Role Checkboxes */}
      <ScrollView style={styles.roleFilterList} nestedScrollEnabled>
        {roles?.map(role => (
          <TouchableOpacity
            key={role.id}
            style={styles.roleFilterItem}
            onPress={() => handleRoleFilterToggle(role.id)}
            accessibilityRole="checkbox"
            accessibilityLabel={role.name}
            accessibilityState={{ checked: activeRoleIds.includes(role.id) }}
          >
            <View
              style={[
                styles.filterCheckbox,
                activeRoleIds.includes(role.id) && styles.filterCheckboxChecked,
              ]}
            >
              {activeRoleIds.includes(role.id) && (
                <Text style={styles.filterCheckmark}>✓</Text>
              )}
            </View>
            <Text style={styles.roleFilterItemText}>{role.name}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <TouchableOpacity
        style={styles.roleFilterCloseButton}
        onPress={() => setShowRoleFilterDropdown(false)}
        accessibilityRole="button"
        accessibilityLabel="Close role filter"
      >
        <Text style={styles.roleFilterCloseText}>Done</Text>
      </TouchableOpacity>
    </>
  );

  const renderRowActions = (userRow: UserWithRoles) => {
    if (!(canManageRoles || hasImpersonatorRole)) {
      return null;
    }
    const isEditing = editingUser?.userId === userRow.userId;

    if (!isEditing) {
      const ownRow = isOwnRow(userRow);
      const cannotImpersonate =
        ownRow ||
        isImpersonating ||
        userRow.roles.some(r => PRIVILEGED_ROLE_NAMES.has(r.name));
      return (
        <View style={styles.actionButtons}>
          {canManageRoles && (
            <TouchableOpacity
              testID={`role-edit-btn-${userRow.userId}`}
              style={styles.editIconButton}
              onPress={() => handleEditClick(userRow)}
              disabled={editingUser !== null || isSaving || ownRow}
              accessibilityState={{ disabled: ownRow }}
              accessibilityHint={
                ownRow ? 'You cannot change your own roles' : undefined
              }
            >
              <Edit size={18} color={ownRow ? '#adb5bd' : '#FF6B35'} />
            </TouchableOpacity>
          )}
          {hasImpersonatorRole && (
            <TouchableOpacity
              style={styles.loginAsIconButton}
              onPress={() => handleLoginAsClick(userRow)}
              disabled={cannotImpersonate || isImpersonationLoading}
              accessibilityLabel={`Login as ${
                userRow.firstName || userRow.email
              }`}
              testID={`impersonate-eye-${userRow.userId}`}
            >
              <Eye
                size={18}
                color={cannotImpersonate ? '#adb5bd' : '#0d6efd'}
              />
            </TouchableOpacity>
          )}
        </View>
      );
    }

    return (
      <View style={styles.actionButtons}>
        <TouchableOpacity
          testID={`role-save-btn-${userRow.userId}`}
          style={styles.saveIconButton}
          onPress={() => handleSaveChanges(userRow)}
          disabled={isSaving}
        >
          <Save size={18} color="#28a745" />
        </TouchableOpacity>
        <TouchableOpacity
          testID={`role-cancel-btn-${userRow.userId}`}
          style={styles.cancelIconButton}
          onPress={handleCancelEdit}
          disabled={isSaving}
        >
          <X size={18} color="#666" />
        </TouchableOpacity>
      </View>
    );
  };

  const renderNativeIdentityCard = (userRow: UserWithRoles) => {
    const userRoleNames = new Set(userRow.roles.map(r => r.name));
    const isEditing = editingUser?.userId === userRow.userId;
    const displayRoles =
      isEditing && editingUser ? editingUser.selectedRoles : userRoleNames;

    return (
      <View
        testID={`user-row-${userRow.userId}`}
        style={styles.nativeIdentityCard}
      >
        <View
          testID={`users-identity-freeze-bar-${userRow.userId}`}
          style={styles.nativeIdentityFreezeBar}
        />
        <View style={styles.nativeIdentityBody}>
          <View style={styles.nativeIdentityHeader}>
            <View
              testID={`users-table-user-cell-${userRow.userId}`}
              style={styles.nativeIdentityText}
            >
              <Text style={styles.userName} numberOfLines={1}>
                {getUserListDisplayName(userRow)}
              </Text>
              {(userRow.firstName || userRow.lastName) && (
                <Text style={styles.userEmail} numberOfLines={1}>
                  {userRow.email}
                </Text>
              )}
              <Text style={styles.nativeIdentityMeta} numberOfLines={1}>
                Created{' '}
                {userRow.createdAt
                  ? dateFormatting.format(userRow.createdAt) ?? 'N/A'
                  : 'N/A'}
                {' · '}
                Last login{' '}
                {userRow.lastLoginAt
                  ? dateFormatting.format(userRow.lastLoginAt) ?? 'Never'
                  : 'Never'}
              </Text>
            </View>
            {renderRowActions(userRow)}
          </View>
          {canManageRoles && (tableRoleColumns ?? []).length > 0 ? (
            <ScrollView
              testID={`users-identity-role-scroll-${userRow.userId}`}
              horizontal
              nestedScrollEnabled
              directionalLockEnabled={Platform.OS === 'ios'}
              showsHorizontalScrollIndicator
              style={styles.nativeIdentityRoleScroll}
              contentContainerStyle={styles.nativeIdentityRoleContent}
            >
              {tableRoleColumns?.map(col => {
                if (col.type === 'lvo') {
                  const hasLvo = isLvoColumnChecked(displayRoles);
                  const isDisabled =
                    !isEditing ||
                    isSaving ||
                    !hasLvoEligibleChannelRole(displayRoles);

                  return (
                    <TouchableOpacity
                      key={LVO_COLUMN_KEY}
                      testID="role-checkbox-lvo"
                      style={[
                        styles.nativeIdentityRoleChip,
                        hasLvo && styles.nativeIdentityRoleChipActive,
                      ]}
                      onPress={handleLvoToggleWhileEditing}
                      disabled={isDisabled}
                      accessibilityState={{
                        checked: hasLvo,
                        disabled: isDisabled,
                      }}
                    >
                      <View
                        style={[
                          styles.checkbox,
                          hasLvo && styles.checkboxChecked,
                          isDisabled && styles.checkboxDisabled,
                        ]}
                      >
                        {hasLvo && <Text style={styles.checkmark}>✓</Text>}
                      </View>
                      <Text
                        style={[
                          styles.nativeIdentityRoleChipText,
                          hasLvo && styles.nativeIdentityRoleChipTextActive,
                        ]}
                        numberOfLines={1}
                      >
                        {LVO_COLUMN_LABEL}
                      </Text>
                    </TouchableOpacity>
                  );
                }

                const roleName = col.role.name as AppRole;
                const hasRole = displayRoles.has(roleName);
                const isDisabled =
                  !isEditing || isSaving || isRoleLockedForCaller(roleName);

                return (
                  <TouchableOpacity
                    key={roleName}
                    testID={`role-checkbox-${roleName
                      .replace(/\s+/g, '-')
                      .toLowerCase()}`}
                    style={[
                      styles.nativeIdentityRoleChip,
                      hasRole && styles.nativeIdentityRoleChipActive,
                    ]}
                    onPress={() => handleRoleToggleWhileEditing(roleName)}
                    disabled={isDisabled}
                    accessibilityState={{
                      checked: hasRole,
                      disabled: isDisabled,
                    }}
                  >
                    <View
                      style={[
                        styles.checkbox,
                        hasRole && styles.checkboxChecked,
                        isDisabled && styles.checkboxDisabled,
                      ]}
                    >
                      {hasRole && <Text style={styles.checkmark}>✓</Text>}
                    </View>
                    <Text
                      style={[
                        styles.nativeIdentityRoleChipText,
                        hasRole && styles.nativeIdentityRoleChipTextActive,
                      ]}
                      numberOfLines={1}
                    >
                      {col.role.name}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          ) : null}
        </View>
      </View>
    );
  };

  // Shared table header cells
  const renderTableHeaderCells = () => (
    <>
      {(canManageRoles || hasImpersonatorRole) && (
        <View style={[styles.headerCell, styles.actionColumn]} />
      )}
      <View
        testID="users-table-user-header"
        style={[
          styles.userColumn,
          freezeUserColumn && styles.frozenUserHeaderCell,
          freezeUserColumn && Platform.OS === 'web' && FROZEN_USER_HEADER_WEB,
        ]}
      >
        <Text style={styles.headerCell}>User</Text>
      </View>
      {canManageRoles &&
        tableRoleColumns?.map(col =>
          col.type === 'lvo' ? (
            <Text
              key={LVO_COLUMN_KEY}
              style={[styles.headerCell, styles.roleColumn]}
              accessibilityLabel={LVO_COLUMN_ACCESSIBILITY_LABEL}
              accessibilityRole="header"
              numberOfLines={2}
            >
              {LVO_COLUMN_LABEL}
            </Text>
          ) : (
            <Text
              key={col.role.id}
              style={[styles.headerCell, styles.roleColumn]}
              accessibilityLabel={col.role.name}
              accessibilityRole="header"
              numberOfLines={2}
              adjustsFontSizeToFit
              minimumFontScale={0.75}
            >
              {formatTwoLineTableHeader(col.role.name)}
            </Text>
          ),
        )}
      <Text
        style={[styles.headerCell, styles.dateColumn]}
        accessibilityLabel="Created Date"
        accessibilityRole="header"
        numberOfLines={2}
      >
        {formatTwoLineTableHeader('Created Date')}
      </Text>
      <Text
        style={[styles.headerCell, styles.dateColumn]}
        accessibilityLabel="Last Login"
        accessibilityRole="header"
        numberOfLines={2}
      >
        {formatTwoLineTableHeader('Last Login')}
      </Text>
    </>
  );

  // Shared table body rows
  const renderTableRows = () => (
    <>
      {users.length === 0 ? (
        <View testID="users-table-empty-state" style={styles.emptyState}>
          <Text style={styles.emptyText}>No users found</Text>
        </View>
      ) : (
        users.map(userRow => {
          const userRoleNames = new Set(userRow.roles.map(r => r.name));
          const isEditing = editingUser?.userId === userRow.userId;
          const displayRoles =
            isEditing && editingUser
              ? editingUser.selectedRoles
              : userRoleNames;

          return (
            <React.Fragment key={`user-${userRow.userId}`}>
              <View
                testID={`user-row-${userRow.userId}`}
                style={styles.tableRow}
              >
                {(canManageRoles || hasImpersonatorRole) && (
                  <View style={[styles.cell, styles.actionColumn]}>
                    {renderRowActions(userRow)}
                  </View>
                )}

                <View
                  testID={`users-table-user-cell-${userRow.userId}`}
                  style={[
                    styles.cell,
                    styles.userColumn,
                    freezeUserColumn && styles.frozenUserBodyCell,
                    freezeUserColumn &&
                      Platform.OS === 'web' &&
                      FROZEN_USER_COLUMN_WEB,
                  ]}
                >
                  <Text style={styles.userName} numberOfLines={1}>
                    {getUserListDisplayName(userRow)}
                  </Text>
                  {(userRow.firstName || userRow.lastName) && (
                    <Text style={styles.userEmail} numberOfLines={1}>
                      {userRow.email}
                    </Text>
                  )}
                </View>

                {/* Role Checkboxes */}
                {canManageRoles &&
                  tableRoleColumns?.map(col => {
                    if (col.type === 'lvo') {
                      const hasLvo = isLvoColumnChecked(displayRoles);
                      const isDisabled =
                        !isEditing ||
                        isSaving ||
                        !hasLvoEligibleChannelRole(displayRoles);

                      return (
                        <View
                          key={LVO_COLUMN_KEY}
                          style={[styles.cell, styles.roleColumn]}
                        >
                          <TouchableOpacity
                            testID="role-checkbox-lvo"
                            style={styles.checkboxContainer}
                            onPress={handleLvoToggleWhileEditing}
                            disabled={isDisabled}
                          >
                            <View
                              style={[
                                styles.checkbox,
                                hasLvo && styles.checkboxChecked,
                                isDisabled && styles.checkboxDisabled,
                              ]}
                            >
                              {hasLvo && (
                                <Text style={styles.checkmark}>✓</Text>
                              )}
                            </View>
                          </TouchableOpacity>
                        </View>
                      );
                    }

                    const roleName = col.role.name as AppRole;
                    const hasRole = displayRoles.has(roleName);
                    const isDisabled =
                      !isEditing || isSaving || isRoleLockedForCaller(roleName);

                    return (
                      <View
                        key={roleName}
                        style={[styles.cell, styles.roleColumn]}
                      >
                        <TouchableOpacity
                          testID={`role-checkbox-${roleName
                            .replace(/\s+/g, '-')
                            .toLowerCase()}`}
                          style={styles.checkboxContainer}
                          onPress={() => handleRoleToggleWhileEditing(roleName)}
                          disabled={isDisabled}
                        >
                          <View
                            style={[
                              styles.checkbox,
                              hasRole && styles.checkboxChecked,
                              isDisabled && styles.checkboxDisabled,
                            ]}
                          >
                            {hasRole && <Text style={styles.checkmark}>✓</Text>}
                          </View>
                        </TouchableOpacity>
                      </View>
                    );
                  })}

                {/* Created Date - Ticket #810 */}
                <Text style={[styles.cell, styles.dateColumn]}>
                  {userRow.createdAt
                    ? dateFormatting.format(userRow.createdAt) ?? 'N/A'
                    : 'N/A'}
                </Text>

                {/* Last Login */}
                <Text style={[styles.cell, styles.dateColumn]}>
                  {userRow.lastLoginAt
                    ? dateFormatting.format(userRow.lastLoginAt) ?? 'Never'
                    : 'Never'}
                </Text>
              </View>
            </React.Fragment>
          );
        })
      )}
    </>
  );

  return (
    <View style={styles.container}>
      <Header currentPath={currentPath || '/users'} navigate={navigate} />
      <SubHeader title="Users" onNavigate={navigate} />

      {/* Tab Navigation — only show when multiple tabs are visible */}
      {canManageDefaultSponsors && (
        <View style={styles.tabContainer} accessibilityRole="tablist">
          <TouchableOpacity
            style={[styles.tab, activeTab === 'roles' && styles.tabActive]}
            onPress={() => setActiveTab('roles')}
            accessibilityRole="tab"
            accessibilityLabel="Role Management"
            accessibilityState={{ selected: activeTab === 'roles' }}
            testID="tab-users"
          >
            <Text
              style={[
                styles.tabText,
                activeTab === 'roles' && styles.tabTextActive,
              ]}
            >
              Role Management
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'sponsors' && styles.tabActive]}
            onPress={() => setActiveTab('sponsors')}
            accessibilityRole="tab"
            accessibilityLabel="Default Sponsors"
            accessibilityState={{ selected: activeTab === 'sponsors' }}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === 'sponsors' && styles.tabTextActive,
              ]}
            >
              Default Sponsors
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'groups' && styles.tabActive]}
            onPress={() => setActiveTab('groups')}
            accessibilityRole="tab"
            accessibilityLabel="Groups"
            accessibilityState={{ selected: activeTab === 'groups' }}
            testID="tab-groups"
          >
            <Text
              style={[
                styles.tabText,
                activeTab === 'groups' && styles.tabTextActive,
              ]}
            >
              Groups
            </Text>
          </TouchableOpacity>
        </View>
      )}

      <View style={styles.content}>
        {/* Role Management Tab Content */}
        {visibleActiveTab === 'roles' && (
          <>
            {/* Search and Controls */}
            <View style={styles.controlsContainer}>
              <View style={styles.searchContainer}>
                <Search size={20} color="#666" style={styles.searchIcon} />
                <TextInput
                  style={styles.searchInput}
                  placeholder="Search by name or email..."
                  value={searchText}
                  onChangeText={setSearchText}
                  placeholderTextColor="#999"
                  returnKeyType="search"
                  testID="users-search-input"
                  inputAccessoryViewID={
                    Platform.OS === 'ios' ? ACCESSORY_VIEW_ID : undefined
                  }
                />
              </View>

              <TouchableOpacity
                style={styles.refreshButton}
                onPress={handleRefresh}
                disabled={isFetching}
              >
                {isFetching ? (
                  <ActivityIndicator size="small" color="#FF6B35" />
                ) : (
                  <RefreshCw size={20} color="#666" />
                )}
                <Text style={styles.refreshText}>
                  {isFetching ? 'Loading...' : 'Refresh'}
                </Text>
              </TouchableOpacity>
            </View>

            {/* Filter Row */}
            <View style={styles.filterContainer}>
              {/* External Users Only Toggle */}
              <TouchableOpacity
                style={[
                  styles.filterButton,
                  externalOnly && styles.filterButtonActive,
                ]}
                onPress={() => {
                  setExternalOnly(!externalOnly);
                  setPage(1);
                }}
              >
                <View
                  style={[
                    styles.filterCheckbox,
                    externalOnly && styles.filterCheckboxChecked,
                  ]}
                >
                  {externalOnly && (
                    <Text style={styles.filterCheckmark}>✓</Text>
                  )}
                </View>
                <Text
                  style={[
                    styles.filterButtonText,
                    externalOnly && styles.filterButtonTextActive,
                  ]}
                >
                  External Users Only
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                testID="users-freeze-user-column-toggle"
                style={[
                  styles.filterButton,
                  freezeUserColumn && styles.filterButtonActive,
                ]}
                onPress={() => setFreezeUserColumn(value => !value)}
                accessibilityRole="switch"
                accessibilityLabel="Freeze User column"
                accessibilityState={{ checked: freezeUserColumn }}
              >
                <View
                  style={[
                    styles.filterCheckbox,
                    freezeUserColumn && styles.filterCheckboxChecked,
                  ]}
                >
                  {freezeUserColumn && (
                    <Text style={styles.filterCheckmark}>✓</Text>
                  )}
                </View>
                <Text
                  style={[
                    styles.filterButtonText,
                    freezeUserColumn && styles.filterButtonTextActive,
                  ]}
                >
                  Freeze User column
                </Text>
              </TouchableOpacity>

              {/* Role Filter Dropdown */}
              <View
                ref={roleFilterDropdownRef}
                style={styles.filterDropdownContainer}
                // @ts-ignore - data attribute for web click-outside detection
                data-role-filter-dropdown="true"
              >
                <TouchableOpacity
                  style={[
                    styles.filterButton,
                    (selectedRoleIds.length > 0 || excludeRoleIds.length > 0) &&
                      styles.filterButtonActive,
                  ]}
                  onPress={() =>
                    setShowRoleFilterDropdown(!showRoleFilterDropdown)
                  }
                >
                  <Filter
                    size={16}
                    color={
                      selectedRoleIds.length > 0 || excludeRoleIds.length > 0
                        ? '#FF6B35'
                        : '#666'
                    }
                  />
                  <Text
                    style={[
                      styles.filterButtonText,
                      (selectedRoleIds.length > 0 ||
                        excludeRoleIds.length > 0) &&
                        styles.filterButtonTextActive,
                    ]}
                  >
                    {selectedRoleIds.length > 0
                      ? `Include ${selectedRoleIds.length} role${
                          selectedRoleIds.length > 1 ? 's' : ''
                        }`
                      : excludeRoleIds.length > 0
                      ? `Exclude ${excludeRoleIds.length} role${
                          excludeRoleIds.length > 1 ? 's' : ''
                        }`
                      : 'Filter by Role'}
                  </Text>
                  <ChevronDown size={16} color="#666" />
                </TouchableOpacity>

                {showRoleFilterDropdown && isWeb && (
                  <View style={styles.roleFilterDropdown}>
                    {renderRoleFilterContent()}
                  </View>
                )}
                {!isWeb && (
                  <Modal
                    visible={showRoleFilterDropdown}
                    transparent
                    animationType="fade"
                    onRequestClose={() => setShowRoleFilterDropdown(false)}
                  >
                    <TouchableOpacity
                      style={styles.roleFilterModalOverlay}
                      activeOpacity={1}
                      onPress={() => setShowRoleFilterDropdown(false)}
                    >
                      <View
                        style={styles.roleFilterModalContent}
                        onStartShouldSetResponder={() => true}
                      >
                        {renderRoleFilterContent()}
                      </View>
                    </TouchableOpacity>
                  </Modal>
                )}
              </View>

              {/* Last Login Filter Dropdown */}
              <View style={styles.filterDropdownContainer}>
                <TouchableOpacity
                  style={[
                    styles.filterButton,
                    lastLoginWithin !== '' && styles.filterButtonActive,
                  ]}
                  onPress={() => {
                    // Cycle through options or use a dropdown
                    const currentIndex = LAST_LOGIN_OPTIONS.findIndex(
                      opt => opt.value === lastLoginWithin,
                    );
                    const nextIndex =
                      (currentIndex + 1) % LAST_LOGIN_OPTIONS.length;
                    setLastLoginWithin(LAST_LOGIN_OPTIONS[nextIndex].value);
                    setPage(1);
                  }}
                >
                  <Text
                    style={[
                      styles.filterButtonText,
                      lastLoginWithin !== '' && styles.filterButtonTextActive,
                    ]}
                  >
                    {LAST_LOGIN_OPTIONS.find(
                      opt => opt.value === lastLoginWithin,
                    )?.label || 'Last Login'}
                  </Text>
                  <ChevronDown
                    size={16}
                    color={lastLoginWithin !== '' ? '#FF6B35' : '#666'}
                  />
                </TouchableOpacity>
              </View>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <TouchableOpacity
                  style={styles.clearFiltersButton}
                  onPress={clearAllFilters}
                >
                  <X size={16} color="#666" />
                  <Text style={styles.clearFiltersText}>Clear filters</Text>
                </TouchableOpacity>
              )}
            </View>

            {/* Error State */}
            {error && (
              <View style={styles.errorContainer}>
                <AlertCircle size={20} color="#dc3545" />
                <Text style={styles.errorText}>
                  {error instanceof Error
                    ? error.message
                    : 'Failed to load users'}
                </Text>
              </View>
            )}

            {/* Loading State - only show on initial load when no data exists */}
            {isLoading && !usersData && (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#FF6B35" />
                <Text style={styles.loadingText}>Loading users...</Text>
              </View>
            )}

            {/* Table - one horizontal scroll owner keeps headers and rows aligned.
                Native freeze uses identity cards instead of sticky-left. */}
            {!error && usersData && (
              <View
                testID="users-table-container"
                style={[
                  styles.tableContainer,
                  !isNativeIdentityFreeze && styles.tableIndicatorGutter,
                  isFetching && styles.tableLoading,
                ]}
              >
                {isNativeIdentityFreeze ? (
                  <ScrollView
                    testID="users-native-identity-list"
                    style={styles.nativeIdentityList}
                    contentContainerStyle={styles.nativeIdentityListContent}
                    nestedScrollEnabled
                    showsVerticalScrollIndicator
                  >
                    <View testID="users-table">
                      {users.length === 0 ? (
                        <View
                          testID="users-table-empty-state"
                          style={styles.emptyState}
                        >
                          <Text style={styles.emptyText}>No users found</Text>
                        </View>
                      ) : (
                        users.map(userRow => (
                          <React.Fragment key={`user-${userRow.userId}`}>
                            {renderNativeIdentityCard(userRow)}
                          </React.Fragment>
                        ))
                      )}
                    </View>
                  </ScrollView>
                ) : (
                  <>
                    {isWebUsersTable ? (
                      <View
                        ref={tableScrollportRef}
                        testID="users-table-scrollport"
                        style={[
                          styles.tableHorizontalScroll,
                          WEB_TABLE_SCROLLPORT_STYLE,
                        ]}
                        onLayout={event => {
                          const { width, height } = event.nativeEvent.layout;
                          tableViewportHeightRef.current = height;
                          setTableViewportWidth(width);
                          setTableBodyViewportHeight(
                            Math.max(height - tableHeaderHeightRef.current, 0),
                          );
                        }}
                        {...({
                          onScroll: (
                            event: Parameters<typeof readTableScrollOffset>[0],
                          ) => {
                            tableScrollIndicatorRef.current?.setHorizontalOffset(
                              readTableScrollOffset(event, 'x'),
                            );
                            tableScrollIndicatorRef.current?.setVerticalOffset(
                              readTableScrollOffset(event, 'y'),
                            );
                          },
                        } as Record<string, unknown>)}
                      >
                        <View
                          testID="users-table"
                          style={[styles.table, { minWidth: tableMinWidth }]}
                          onLayout={event => {
                            const { width, height } = event.nativeEvent.layout;
                            tableHeightRef.current = height;
                            setTableContentWidth(width);
                            setTableBodyContentHeight(
                              Math.max(
                                height - tableHeaderHeightRef.current,
                                0,
                              ),
                            );
                          }}
                        >
                          <View
                            testID="users-table-header"
                            style={[
                              styles.tableHeader,
                              WEB_TABLE_HEADER_STICKY,
                            ]}
                            onLayout={event => {
                              const height = event.nativeEvent.layout.height;
                              tableHeaderHeightRef.current = height;
                              setTableHeaderHeight(height);
                              setTableBodyViewportHeight(
                                Math.max(
                                  tableViewportHeightRef.current - height,
                                  0,
                                ),
                              );
                              setTableBodyContentHeight(
                                Math.max(tableHeightRef.current - height, 0),
                              );
                            }}
                          >
                            {renderTableHeaderCells()}
                          </View>
                          <View
                            testID="users-table-body"
                            style={styles.tableBodyContent}
                          >
                            {renderTableRows()}
                          </View>
                        </View>
                      </View>
                    ) : (
                      <ScrollView
                        ref={tableHorizontalScrollRef}
                        testID="users-table-horizontal-scroll"
                        style={styles.tableHorizontalScroll}
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        directionalLockEnabled={Platform.OS === 'ios'}
                        scrollEventThrottle={16}
                        contentContainerStyle={styles.tableContentContainer}
                        onLayout={event =>
                          setTableViewportWidth(event.nativeEvent.layout.width)
                        }
                        onContentSizeChange={(width, _height) =>
                          setTableContentWidth(width)
                        }
                        onScroll={event =>
                          tableScrollIndicatorRef.current?.setHorizontalOffset(
                            event.nativeEvent.contentOffset.x,
                          )
                        }
                      >
                        <View
                          testID="users-table"
                          style={[styles.table, { minWidth: tableMinWidth }]}
                        >
                          <View
                            testID="users-table-header"
                            style={styles.tableHeader}
                            onLayout={event =>
                              setTableHeaderHeight(
                                event.nativeEvent.layout.height,
                              )
                            }
                          >
                            {renderTableHeaderCells()}
                          </View>
                          <ScrollView
                            ref={tableVerticalScrollRef}
                            testID="users-table-body"
                            style={styles.tableBodyScroll}
                            contentContainerStyle={styles.tableBodyContent}
                            nestedScrollEnabled
                            showsVerticalScrollIndicator={false}
                            scrollEventThrottle={16}
                            onLayout={event =>
                              setTableBodyViewportHeight(
                                event.nativeEvent.layout.height,
                              )
                            }
                            onContentSizeChange={(_width, height) =>
                              setTableBodyContentHeight(height)
                            }
                            onScroll={event =>
                              tableScrollIndicatorRef.current?.setVerticalOffset(
                                event.nativeEvent.contentOffset.y,
                              )
                            }
                          >
                            {renderTableRows()}
                          </ScrollView>
                        </View>
                      </ScrollView>
                    )}
                    <TableScrollIndicators
                      ref={tableScrollIndicatorRef}
                      bodyContentHeight={tableBodyContentHeight}
                      bodyViewportHeight={tableBodyViewportHeight}
                      contentWidth={tableContentWidth}
                      headerHeight={tableHeaderHeight}
                      onHorizontalScrollTo={offset =>
                        isWebUsersTable
                          ? scrollWebTable('horizontal', offset)
                          : tableHorizontalScrollRef.current?.scrollTo({
                              x: offset,
                              animated: false,
                            })
                      }
                      onVerticalScrollTo={offset =>
                        isWebUsersTable
                          ? scrollWebTable('vertical', offset)
                          : tableVerticalScrollRef.current?.scrollTo({
                              y: offset,
                              animated: false,
                            })
                      }
                      viewportWidth={tableViewportWidth}
                    />
                  </>
                )}
              </View>
            )}

            {/* Pagination */}
            {!error && pagination && (
              <View style={styles.paginationContainer}>
                <View style={styles.paginationInfo}>
                  <Text style={styles.paginationText}>
                    Showing {users.length > 0 ? (page - 1) * pageSize + 1 : 0}-
                    {Math.min(page * pageSize, pagination.totalCount)} of{' '}
                    {pagination.totalCount} users
                  </Text>
                </View>

                <View style={styles.paginationControls}>
                  <Text style={styles.paginationLabel}>Rows per page:</Text>
                  <TouchableOpacity
                    style={styles.pageSizeButton}
                    onPress={() =>
                      handlePageSizeChange(
                        pageSize === 20 ? 50 : pageSize === 50 ? 100 : 20,
                      )
                    }
                  >
                    <Text style={styles.pageSizeText}>{pageSize}</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[
                      styles.pageButton,
                      !hasPrevPage && styles.pageButtonDisabled,
                    ]}
                    onPress={() => setPage(page - 1)}
                    disabled={!hasPrevPage}
                  >
                    <Text
                      style={[
                        styles.pageButtonText,
                        !hasPrevPage && styles.pageButtonTextDisabled,
                      ]}
                    >
                      Previous
                    </Text>
                  </TouchableOpacity>

                  <Text style={styles.pageInfo}>
                    Page {page} of {totalPages}
                  </Text>

                  <TouchableOpacity
                    style={[
                      styles.pageButton,
                      !hasNextPage && styles.pageButtonDisabled,
                    ]}
                    onPress={() => setPage(page + 1)}
                    disabled={!hasNextPage}
                  >
                    <Text
                      style={[
                        styles.pageButtonText,
                        !hasNextPage && styles.pageButtonTextDisabled,
                      ]}
                    >
                      Next
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </>
        )}

        {/* Default Sponsors Tab Content */}
        {visibleActiveTab === 'sponsors' &&
          canManageDefaultSponsors &&
          renderDefaultSponsorsTab()}
        {visibleActiveTab === 'groups' && canManageDefaultSponsors && (
          <GroupsTab enabled />
        )}
      </View>

      {/* User Selector Modal for Default Sponsors */}
      {activeModal && (
        <Modal
          visible={true}
          transparent
          animationType="fade"
          onRequestClose={() => setActiveModal(null)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContainer}>
              {/* Header */}
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>
                  {getModalTitle()} ({modalSelectedUsers.size} selected)
                </Text>
                <TouchableOpacity
                  style={styles.modalCloseButton}
                  onPress={() => setActiveModal(null)}
                >
                  <X size={24} color="#333" />
                </TouchableOpacity>
              </View>

              {/* Search */}
              <View style={styles.modalSearchContainer}>
                <Search size={20} color="#999" style={styles.searchIcon} />
                <TextInput
                  style={styles.modalSearchInput}
                  placeholder="Search by name or email"
                  value={modalSearchText}
                  onChangeText={setModalSearchText}
                  placeholderTextColor="#999"
                />
              </View>

              {/* User List */}
              <FlatList
                data={(usersData?.users || []).filter(user => {
                  // Filter out users who already have this specific Default Sponsor role
                  const currentSponsors =
                    activeModal === 'nam'
                      ? namSponsors
                      : activeModal === 'emea'
                      ? emeaSponsors
                      : apacSponsors;
                  const alreadyHasRole = currentSponsors.some(
                    s => s.userId === user.userId,
                  );

                  if (alreadyHasRole) return false;

                  // Apply search filter
                  if (!modalSearchText.trim()) return true;
                  const query = modalSearchText.toLowerCase();
                  return (
                    user.email.toLowerCase().includes(query) ||
                    (user.firstName || '').toLowerCase().includes(query) ||
                    (user.lastName || '').toLowerCase().includes(query)
                  );
                })}
                keyExtractor={item => `user-${item.userId}`}
                renderItem={({ item }) => {
                  const isSelected = modalSelectedUsers.has(item.userId);
                  return (
                    <TouchableOpacity
                      style={[
                        styles.modalUserItem,
                        isSelected && styles.modalUserItemSelected,
                      ]}
                      onPress={() => {
                        const newSelected = new Set(modalSelectedUsers);
                        if (isSelected) {
                          newSelected.delete(item.userId);
                        } else {
                          newSelected.add(item.userId);
                        }
                        setModalSelectedUsers(newSelected);
                      }}
                    >
                      <View style={styles.modalUserInfo}>
                        <Text style={styles.modalUserName}>
                          {item.firstName || item.lastName
                            ? `${item.firstName || ''} ${
                                item.lastName || ''
                              }`.trim()
                            : item.email}
                        </Text>
                        {(item.firstName || item.lastName) && (
                          <Text style={styles.modalUserEmail}>
                            {item.email}
                          </Text>
                        )}
                      </View>
                      <View
                        style={[
                          styles.modalCheckbox,
                          isSelected && styles.modalCheckboxSelected,
                        ]}
                      >
                        {isSelected && <Check size={16} color="#fff" />}
                      </View>
                    </TouchableOpacity>
                  );
                }}
                style={styles.modalUserList}
                contentContainerStyle={styles.modalUserListContent}
                showsVerticalScrollIndicator={true}
                nestedScrollEnabled={true}
              />

              {/* Footer */}
              <View style={styles.modalFooter}>
                <TouchableOpacity
                  style={styles.modalCancelButton}
                  onPress={() => setActiveModal(null)}
                >
                  <Text style={styles.modalCancelButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.modalDoneButton}
                  onPress={() =>
                    handleUserSelection(Array.from(modalSelectedUsers))
                  }
                >
                  <Text style={styles.modalDoneButtonText}>
                    Done ({modalSelectedUsers.size})
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      )}

      {/* Impersonation Confirmation Dialog */}
      <Modal
        visible={showConfirmDialog}
        transparent
        animationType="fade"
        onRequestClose={handleCancelImpersonation}
      >
        <View style={styles.impersonationModalOverlay}>
          <View
            style={styles.impersonationModalContent}
            testID="impersonation-confirm-modal"
          >
            <Text style={styles.impersonationModalTitle}>
              Confirm Impersonation
            </Text>
            <Text style={styles.impersonationModalMessage}>
              You are about to impersonate:
            </Text>
            <Text style={styles.impersonationModalUserName}>
              {selectedUserForImpersonation?.displayName ||
                selectedUserForImpersonation?.email}
            </Text>
            <Text style={styles.impersonationModalUserEmail}>
              {selectedUserForImpersonation?.email}
            </Text>
            {hasBothImpersonatorRoles && selectedImpersonationMode && (
              <ImpersonationModePicker
                selectedMode={selectedImpersonationMode}
                onModeChange={setSelectedImpersonationMode}
              />
            )}
            {!hasBothImpersonatorRoles && hasFullAccess && (
              <Text style={styles.impersonationModalModeInfo}>
                Mode: Full Access
              </Text>
            )}
            {!hasBothImpersonatorRoles && hasReadOnly && (
              <Text style={styles.impersonationModalModeInfo}>
                Mode: Read Only
              </Text>
            )}
            <Text style={styles.impersonationModalExpiry}>
              Session will expire in 1 hour
            </Text>

            {impersonationError && (
              <View style={styles.impersonationModalErrorContainer}>
                <AlertCircle size={16} color="#dc3545" />
                <Text style={styles.impersonationModalErrorText}>
                  {impersonationError}
                </Text>
              </View>
            )}

            <View style={styles.impersonationModalActions}>
              <TouchableOpacity
                style={styles.impersonationModalCancelButton}
                onPress={handleCancelImpersonation}
                disabled={isImpersonationLoading}
                accessibilityLabel="Cancel impersonation"
                testID="impersonation-cancel-button"
              >
                <Text style={styles.impersonationModalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.impersonationModalConfirmButton,
                  isImpersonationLoading &&
                    styles.impersonationModalConfirmButtonDisabled,
                ]}
                onPress={handleConfirmImpersonation}
                disabled={isImpersonationLoading}
                accessibilityLabel="Confirm impersonation"
                testID="impersonation-confirm-button"
              >
                {isImpersonationLoading ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.impersonationModalConfirmText}>
                    Confirm
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* iOS Keyboard Dismiss Toolbar */}
      <KeyboardDismissToolbar />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  content: {
    flex: 1,
    padding: 20,
    alignItems: 'center',
  },
  controlsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    gap: 16,
    width: '100%',
    maxWidth: 1600,
  },
  searchContainer: {
    flex: 1,
    maxWidth: 400,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    paddingHorizontal: 12,
    height: 44,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#333',
    ...Platform.select({
      web: {
        outlineStyle: 'none' as const,
        outlineWidth: 0,
      },
    }),
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    paddingHorizontal: 16,
    height: 44,
    gap: 8,
  },
  refreshText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff3cd',
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
    gap: 12,
  },
  errorText: {
    flex: 1,
    fontSize: 14,
    color: '#856404',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 14,
    color: '#666',
  },
  tableLoading: {
    opacity: 0.6,
  },
  tableContainer: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    width: '100%',
    maxWidth: 1600,
    zIndex: 1,
  },
  tableIndicatorGutter: {
    paddingRight: TABLE_INDICATOR_GUTTER,
    paddingBottom: TABLE_INDICATOR_GUTTER,
  },
  tableHeaderScrollGutter: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: TABLE_INDICATOR_GUTTER,
    backgroundColor: TABLE_HEADER_BACKGROUND,
    borderTopRightRadius: 8,
    borderBottomWidth: 2,
    borderBottomColor: TABLE_HEADER_BORDER,
    zIndex: 2,
  },
  tableContentContainer: {
    flexGrow: 0,
    alignItems: 'stretch',
  },
  tableHorizontalScroll: {
    flex: 1,
    width: '100%',
  },
  table: {
    flexGrow: 0,
  },
  tableBodyScroll: {
    flex: 1,
    width: '100%',
    ...Platform.select({
      web: {
        overflowX: 'clip' as unknown as 'hidden',
      },
    }),
  },
  tableBodyContent: {
    width: '100%',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: TABLE_HEADER_BACKGROUND,
    borderBottomWidth: 2,
    borderBottomColor: TABLE_HEADER_BORDER,
    paddingVertical: TABLE_SECTION_VERTICAL_PADDING,
    paddingHorizontal: TABLE_SECTION_HORIZONTAL_PADDING,
    alignItems: 'center',
  },
  tableVerticalScrollTrack: {
    position: 'absolute',
    right: 1,
    width: TABLE_SCROLL_TRACK_SIZE,
    borderRadius: TABLE_SCROLL_TRACK_RADIUS,
    backgroundColor: TABLE_SCROLL_TRACK_COLOR,
    zIndex: 3,
  },
  tableVerticalScrollThumb: {
    position: 'absolute',
    left: 2,
    width: TABLE_SCROLL_THUMB_SIZE,
    borderRadius: TABLE_SCROLL_THUMB_RADIUS,
    backgroundColor: TABLE_SCROLL_THUMB_COLOR,
  },
  tableHorizontalScrollTrack: {
    position: 'absolute',
    bottom: 1,
    left: 0,
    height: TABLE_SCROLL_TRACK_SIZE,
    borderRadius: TABLE_SCROLL_TRACK_RADIUS,
    backgroundColor: TABLE_SCROLL_TRACK_COLOR,
    zIndex: 3,
  },
  tableHorizontalScrollThumb: {
    position: 'absolute',
    top: 2,
    height: TABLE_SCROLL_THUMB_SIZE,
    borderRadius: TABLE_SCROLL_THUMB_RADIUS,
    backgroundColor: TABLE_SCROLL_THUMB_COLOR,
  },
  headerCell: {
    fontSize: 13,
    fontWeight: '600',
    color: '#495057',
  },
  actionColumn: {
    width: ACTION_COLUMN_WIDTH,
    flexShrink: 0,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  userColumn: {
    width: USER_COLUMN_WIDTH,
    flexShrink: 0,
    flexDirection: 'column',
    justifyContent: 'center',
  },
  frozenUserHeaderCell: {
    zIndex: 2,
    backgroundColor: TABLE_HEADER_BACKGROUND,
    alignSelf: 'stretch',
    marginVertical: -TABLE_SECTION_VERTICAL_PADDING,
    paddingVertical: TABLE_SECTION_VERTICAL_PADDING,
    paddingLeft: TABLE_SECTION_HORIZONTAL_PADDING,
  },
  frozenUserBodyCell: {
    zIndex: 2,
    backgroundColor: '#fff',
    alignSelf: 'stretch',
    marginVertical: -TABLE_SECTION_VERTICAL_PADDING,
    paddingVertical: TABLE_SECTION_VERTICAL_PADDING,
    paddingLeft: TABLE_SECTION_HORIZONTAL_PADDING,
  },
  userName: {
    fontSize: 13,
    fontWeight: '500',
    color: '#212529',
  },
  userEmail: {
    fontSize: 11,
    color: '#6c757d',
    marginTop: 2,
  },
  dateColumn: {
    width: DATE_COLUMN_WIDTH,
    flexShrink: 0,
  },
  roleColumn: {
    width: ROLE_COLUMN_WIDTH,
    flexShrink: 0,
    textAlign: 'center',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
    paddingVertical: TABLE_SECTION_VERTICAL_PADDING,
    paddingHorizontal: TABLE_SECTION_HORIZONTAL_PADDING,
    alignItems: 'center',
  },
  nativeIdentityList: {
    flex: 1,
  },
  nativeIdentityListContent: {
    flexGrow: 1,
  },
  nativeIdentityCard: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 2, height: 0 },
        shadowOpacity: 0.08,
        shadowRadius: 4,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  nativeIdentityFreezeBar: {
    width: 4,
    backgroundColor: '#FF6B35',
  },
  nativeIdentityBody: {
    flex: 1,
    minWidth: 0,
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  nativeIdentityHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  nativeIdentityText: {
    flex: 1,
    minWidth: 0,
  },
  nativeIdentityMeta: {
    fontSize: 11,
    color: '#6c757d',
    marginTop: 4,
  },
  nativeIdentityRoleScroll: {
    marginTop: 10,
  },
  nativeIdentityRoleContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingRight: 16,
  },
  nativeIdentityRoleChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#dee2e6',
    backgroundColor: '#f8f9fa',
  },
  nativeIdentityRoleChipActive: {
    borderColor: '#FF6B35',
    backgroundColor: '#FFF5F2',
  },
  nativeIdentityRoleChipText: {
    fontSize: 12,
    color: '#495057',
  },
  nativeIdentityRoleChipTextActive: {
    color: '#FF6B35',
    fontWeight: '500',
  },
  cell: {
    fontSize: 13,
    color: '#212529',
  },
  checkboxContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    paddingVertical: 4,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: '#adb5bd',
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
  },
  checkboxChecked: {
    backgroundColor: '#FF6B35',
    borderColor: '#FF6B35',
  },
  checkboxDisabled: {
    opacity: 0.5,
  },
  checkmark: {
    fontSize: 14,
    color: '#fff',
    fontWeight: 'bold',
  },
  editIconButton: {
    padding: 8,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  saveIconButton: {
    padding: 8,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelIconButton: {
    padding: 8,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    paddingVertical: 60,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: '#999',
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 20,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#ddd',
    width: '100%',
    maxWidth: 1600,
  },
  paginationInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  paginationText: {
    fontSize: 14,
    color: '#666',
  },
  paginationControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  paginationLabel: {
    fontSize: 14,
    color: '#666',
  },
  pageSizeButton: {
    backgroundColor: '#fff',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    paddingHorizontal: 12,
    paddingVertical: 6,
    minWidth: 50,
    alignItems: 'center',
  },
  pageSizeText: {
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
  },
  pageButton: {
    backgroundColor: '#fff',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  pageButtonDisabled: {
    opacity: 0.5,
  },
  pageButtonText: {
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
  },
  pageButtonTextDisabled: {
    color: '#999',
  },
  pageInfo: {
    fontSize: 14,
    color: '#666',
    marginHorizontal: 8,
  },
  // Filter styles
  filterContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 12,
    width: '100%',
    maxWidth: 1600,
    flexWrap: 'wrap',
    zIndex: 100,
    position: 'relative',
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
  },
  filterButtonActive: {
    borderColor: '#FF6B35',
    backgroundColor: '#FFF5F2',
  },
  filterButtonText: {
    fontSize: 13,
    color: '#666',
  },
  filterButtonTextActive: {
    color: '#FF6B35',
    fontWeight: '500',
  },
  filterCheckbox: {
    width: 16,
    height: 16,
    borderWidth: 2,
    borderColor: '#adb5bd',
    borderRadius: 3,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
  },
  filterCheckboxChecked: {
    backgroundColor: '#FF6B35',
    borderColor: '#FF6B35',
  },
  filterCheckmark: {
    fontSize: 10,
    color: '#fff',
    fontWeight: 'bold',
  },
  filterDropdownContainer: {
    position: 'relative',
    zIndex: 10,
  },
  roleFilterDropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 4,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    minWidth: 220,
    maxHeight: 350,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    zIndex: 100,
  },
  roleFilterModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  roleFilterModalContent: {
    backgroundColor: '#fff',
    borderRadius: 8,
    width: '100%',
    maxWidth: 320,
    maxHeight: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  roleFilterModeContainer: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  roleFilterModeButton: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
  },
  roleFilterModeButtonActive: {
    borderBottomWidth: 2,
    borderBottomColor: '#FF6B35',
  },
  roleFilterModeText: {
    fontSize: 13,
    color: '#666',
  },
  roleFilterModeTextActive: {
    color: '#FF6B35',
    fontWeight: '600',
  },
  roleFilterList: {
    maxHeight: 240,
    paddingVertical: 8,
  },
  roleFilterItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 10,
  },
  roleFilterItemText: {
    fontSize: 13,
    color: '#333',
  },
  roleFilterCloseButton: {
    borderTopWidth: 1,
    borderTopColor: '#eee',
    paddingVertical: 10,
    alignItems: 'center',
  },
  roleFilterCloseText: {
    fontSize: 13,
    color: '#FF6B35',
    fontWeight: '600',
  },
  clearFiltersButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 8,
    gap: 4,
  },
  clearFiltersText: {
    fontSize: 13,
    color: '#666',
  },
  // Regional Roles Styles
  regionalRolesBadge: {
    fontSize: 12,
    color: '#007bff',
    fontWeight: '500',
  },
  expandButton: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    backgroundColor: '#007bff',
    borderRadius: 4,
  },
  expandButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '500',
  },
  emptyCell: {
    fontSize: 14,
    color: '#999',
  },
  regionalRolesAccordion: {
    backgroundColor: '#f8f9fa',
    borderTopWidth: 1,
    borderTopColor: '#dee2e6',
    padding: 16,
    marginTop: 0,
  },
  regionalRolesTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 16,
  },
  regionSection: {
    marginBottom: 16,
  },
  regionLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#495057',
    marginBottom: 8,
  },
  regionRoles: {
    flexDirection: 'row',
    gap: 24,
    paddingLeft: 16,
  },
  regionalRoleItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  regionalRoleLabel: {
    fontSize: 14,
    color: '#333',
  },
  // Tab styles
  tabContainer: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    backgroundColor: '#fff',
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabActive: {
    borderBottomColor: '#FF6B35',
  },
  tabText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  tabTextActive: {
    color: '#FF6B35',
    fontWeight: '600',
  },
  // Default Sponsors Tab Styles
  defaultSponsorsContent: {
    flex: 1,
    width: '100%',
  },
  defaultSponsorsContentContainer: {
    padding: 20,
    ...Platform.select({
      web: { flexGrow: 0 },
      default: {},
    }),
  },
  sponsorGrid: {
    flexDirection: 'row',
    gap: 20,
  },
  sponsorGridMobile: {
    flexDirection: 'column',
  },
  sponsorContainer: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 400,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    backgroundColor: '#fff',
    flexDirection: 'column',
  },
  sponsorContainerMobile: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
    width: '100%',
    minWidth: 0,
  },
  sponsorContainerHeader: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    backgroundColor: '#f8f9fa',
  },
  sponsorList: {
    minHeight: 300,
    maxHeight: 500,
  },
  sponsorListContent: {
    padding: 12,
  },
  sponsorLoadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  sponsorLoadingText: {
    marginTop: 8,
    fontSize: 14,
    color: '#666',
  },
  sponsorEmptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  sponsorEmptyText: {
    fontSize: 14,
    color: '#999',
    fontStyle: 'italic',
  },
  sponsorItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  sponsorItemPending: {
    backgroundColor: '#FFF5F2',
  },
  sponsorItemRemoval: {
    backgroundColor: '#F5F5F5',
    opacity: 0.7,
  },
  sponsorInfo: {
    flex: 1,
  },
  sponsorName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  sponsorNameRemoval: {
    textDecorationLine: 'line-through',
    color: '#999',
  },
  sponsorEmail: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  sponsorEmailRemoval: {
    textDecorationLine: 'line-through',
    color: '#999',
  },
  sponsorPendingLabel: {
    fontSize: 11,
    color: '#FF6B35',
    fontStyle: 'italic',
    marginTop: 2,
  },
  sponsorRemovalLabel: {
    fontSize: 11,
    color: '#FF6B35',
    fontStyle: 'italic',
    marginTop: 2,
  },
  removeButton: {
    padding: 8,
    borderRadius: 4,
  },
  sponsorFooter: {
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    padding: 12,
    backgroundColor: '#f8f9fa',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  sponsorFooterMobile: {
    flexWrap: 'wrap',
  },
  sponsorFooterActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  sponsorFooterButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  sponsorFooterButtonsMobile: {
    marginLeft: 'auto',
  },
  addUserButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#FF6B35',
    backgroundColor: '#fff',
    gap: 8,
    minWidth: 120,
  },
  addUserButtonMobile: {
    minWidth: 0,
    paddingHorizontal: 12,
  },
  addUserButtonText: {
    fontSize: 14,
    color: '#FF6B35',
    fontWeight: '500',
  },
  cancelChangesButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#fff',
  },
  cancelChangesButtonMobile: {
    paddingHorizontal: 12,
  },
  cancelChangesButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  saveChangesButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    backgroundColor: '#FF6B35',
    gap: 8,
  },
  saveChangesButtonMobile: {
    paddingHorizontal: 12,
  },
  saveChangesButtonDisabled: {
    opacity: 0.6,
  },
  saveChangesButtonText: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '600',
  },
  // Login As icon button style
  loginAsIconButton: {
    padding: 8,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // User Selector Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '100%',
    maxWidth: 600,
    ...Platform.select({
      web: {
        maxHeight: '90vh' as any,
        display: 'flex' as any,
        flexDirection: 'column' as any,
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)' as any,
      },
      default: {
        height: '80%',
      },
    }),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    backgroundColor: '#fff',
    ...Platform.select({
      web: {
        flexShrink: 0,
        position: 'sticky' as any,
        top: 0,
        zIndex: 1,
      },
    }),
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  modalCloseButton: {
    padding: 4,
  },
  modalSearchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#f8f9fa',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    ...Platform.select({
      web: {
        flexShrink: 0,
        position: 'sticky' as any,
        top: 57,
        zIndex: 1,
      },
    }),
  },
  modalSearchInput: {
    flex: 1,
    fontSize: 16,
    color: '#333',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    ...Platform.select({
      web: {
        outlineStyle: 'none' as const,
        outlineWidth: 0,
      },
    }),
  },
  modalUserList: {
    flex: 1,
    ...Platform.select({
      web: {
        minHeight: 200,
        maxHeight: 'calc(90vh - 250px)' as any,
        overflow: 'auto' as any,
      },
    }),
  },
  modalUserListContent: {
    paddingVertical: 8,
  },
  modalUserItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  modalUserItemSelected: {
    backgroundColor: '#f0f7ff',
  },
  modalUserInfo: {
    flex: 1,
    marginRight: 12,
  },
  modalUserName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
  },
  modalUserEmail: {
    fontSize: 14,
    color: '#666',
    marginTop: 2,
  },
  modalCheckbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: '#ddd',
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalCheckboxSelected: {
    backgroundColor: '#007bff',
    borderColor: '#007bff',
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    backgroundColor: '#fff',
    ...Platform.select({
      web: {
        flexShrink: 0,
        position: 'sticky' as any,
        bottom: 0,
      },
    }),
  },
  modalCancelButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#fff',
  },
  modalCancelButtonText: {
    fontSize: 16,
    color: '#666',
    fontWeight: '500',
  },
  modalDoneButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 6,
    backgroundColor: '#ff6b35',
  },
  modalDoneButtonText: {
    fontSize: 16,
    color: '#fff',
    fontWeight: '600',
  },
  // Impersonation Modal styles
  impersonationModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  impersonationModalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 24,
    width: '90%',
    maxWidth: 420,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  impersonationModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#212529',
    marginBottom: 12,
  },
  impersonationModalMessage: {
    fontSize: 14,
    color: '#6c757d',
    marginBottom: 8,
  },
  impersonationModalUserName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#212529',
  },
  impersonationModalUserEmail: {
    fontSize: 14,
    color: '#6c757d',
    marginBottom: 12,
  },
  impersonationModalModeInfo: {
    fontSize: 13,
    color: '#495057',
    backgroundColor: '#e9ecef',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    marginBottom: 8,
    overflow: 'hidden',
    fontWeight: '500',
  },
  impersonationModalExpiry: {
    fontSize: 13,
    color: '#856404',
    backgroundColor: '#fff3cd',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    marginBottom: 16,
    overflow: 'hidden',
  },
  impersonationModalErrorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8d7da',
    borderRadius: 6,
    padding: 12,
    marginBottom: 16,
    gap: 8,
  },
  impersonationModalErrorText: {
    flex: 1,
    fontSize: 13,
    color: '#842029',
  },
  impersonationModalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
  impersonationModalCancelButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#dee2e6',
  },
  impersonationModalCancelText: {
    fontSize: 14,
    color: '#6c757d',
    fontWeight: '500',
  },
  impersonationModalConfirmButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 6,
    backgroundColor: '#0d6efd',
    minWidth: 90,
    alignItems: 'center',
  },
  impersonationModalConfirmButtonDisabled: {
    opacity: 0.6,
  },
  impersonationModalConfirmText: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '500',
  },
});
